/* global globalThis */
import * as THREE from 'three'
import { SkeletonUtils } from 'three/examples/jsm/utils/SkeletonUtils.js'
import { FBXLoader, OBJLoader, TGALoader } from '@/utils/legacyThreeLoaders'
import { getSandModelSpec } from '@/config/sandModels'
import { resolveStaticUrl } from '@/config/staticAssets'

const rawFbxCache = Object.create(null)
const rawFbxLoading = Object.create(null)
const variantTemplateCache = Object.create(null)
const variantLoading = Object.create(null)
const VARIANT_CACHE_REV = 212

const TAOZI_MODEL_URL = '/static/models/心理沙盘模型/植物/桃子/taozi.FBX'

if (typeof globalThis !== 'undefined' && globalThis.__SAND_VARIANT_REV__ !== VARIANT_CACHE_REV) {
  Object.keys(rawFbxCache).forEach((key) => {
    delete rawFbxCache[key]
  })
  Object.keys(rawFbxLoading).forEach((key) => {
    delete rawFbxLoading[key]
  })
  Object.keys(variantTemplateCache).forEach((key) => {
    delete variantTemplateCache[key]
  })
  Object.keys(variantLoading).forEach((key) => {
    delete variantLoading[key]
  })
  globalThis.__SAND_VARIANT_REV__ = VARIANT_CACHE_REV
}

function variantCacheKey (spec) {
  if (!spec) return null
  return `${spec.key}@v${VARIANT_CACHE_REV}`
}

function hasWorkingTexture (texture) {
  if (!texture) return false
  const img = texture.image
  if (!img) return false
  if (img.width > 0 && img.height > 0) return true
  return !!(img.data && img.height > 0)
}

function hasWorkingMap (mat) {
  if (!mat || !mat.map) return false
  return hasWorkingTexture(mat.map)
}

const SYNC_PLACEHOLDER_TEXTURE = {
  load: function () {
    return new THREE.Texture()
  }
}

function addEmbeddedTextureHandlers (manager) {
  if (!manager || typeof manager.addHandler !== 'function') return
  manager.addHandler(/\.tga$/i, SYNC_PLACEHOLDER_TEXTURE)
  manager.addHandler(/\.(jpg|jpeg|png|psd|bmp|webp)$/i, SYNC_PLACEHOLDER_TEXTURE)
}

function createFbxLoader (url, manager) {
  const base = url.replace(/[^/]+$/, '')
  addEmbeddedTextureHandlers(manager)
  if (!FBXLoader) throw new Error('FBXLoader not available')
  const loader = new FBXLoader(manager)
  if (base) loader.setResourcePath(base)
  return loader
}

function rawFbxCacheKey (url, textureRedirect) {
  return textureRedirect ? `${url}@${textureRedirect}` : url
}

function pickAnimationClip (clips) {
  if (!clips || !clips.length) return null
  const preferred = clips.find((c) => /idle|stand|walk|run|anim|mixamo/i.test(c.name))
  return preferred || clips[0]
}

/** 男孩：所有动画直接用第一个 clip（模型自带首帧姿态），与女孩保持一致 */
function resolveBoyAnimationClip (clips, spec) {
  if (!clips || !clips.length) return null
  return clips[0] || null
}

function pickSpecAnimationClip (clips, spec) {
  if (!spec || !spec.flattenSkinnedMesh) return pickAnimationClip(clips)
  if (spec.scaleMesh === 'boy') return resolveBoyAnimationClip(clips, spec)
  // 女生 Connie_*.FBX 各文件仅含一条 Take 001
  return clips[0] || pickAnimationClip(clips)
}

function guessPartColor (mesh, material) {
  const label = `${mesh.name || ''} ${material && material.name ? material.name : ''}`.toLowerCase()
  if (/hair|cap|hat|helm/.test(label)) return 0x3e2723
  if (/eye|pupil/.test(label)) return 0x111111
  if (/shirt|cloth|coat|jacket|top|body/.test(label)) return 0x4a90d9
  if (/pant|trouser|leg|shoe|foot|boot/.test(label)) return 0x455a64
  if (/skin|face|hand|arm|head|boy|human/.test(label)) return 0xffcc99
  if (/chicken|bird|feather|wing|comb|beak/.test(label)) return 0xf5d042
  return 0xffcc99
}

function buildFallbackMaterial (mesh, oldMat) {
  const skinning = !!mesh.isSkinnedMesh
  const color = guessPartColor(mesh, oldMat)
  return new THREE.MeshLambertMaterial({
    color,
    skinning,
    side: THREE.FrontSide,
    polygonOffset: true,
    polygonOffsetFactor: 1,
    polygonOffsetUnits: 4,
    fog: false
  })
}

function toDisplayMaterial (mesh, mat) {
  if (!mat || !hasWorkingMap(mat)) {
    return buildFallbackMaterial(mesh, mat)
  }

  const map = mat.map
  if (THREE.sRGBEncoding) map.encoding = THREE.sRGBEncoding
  map.needsUpdate = true
  if (mat.alphaMap && THREE.sRGBEncoding) mat.alphaMap.encoding = THREE.sRGBEncoding
  if (mat.alphaMap) mat.alphaMap.needsUpdate = true
  const alphaMap = hasWorkingTexture(mat.alphaMap) ? mat.alphaMap : null
  const cutout = alphaMap
    ? {
      transparent: true,
      alphaTest: mat.alphaTest > 0 ? mat.alphaTest : 0.08,
      alphaMap,
      depthWrite: true,
      side: THREE.DoubleSide
    }
    : { side: THREE.FrontSide }

  return createLambertMaterialFromTexture(map, !!mesh.isSkinnedMesh, {
    unlit: true,
    ...cutout
  })
}

function replaceMaterial (mesh, mat, index) {
  const next = toDisplayMaterial(mesh, mat)
  if (Array.isArray(mesh.material)) mesh.material[index] = next
  else mesh.material = next
  if (mat && mat !== next) {
    const sharedMap = mat.map && mat.map === next.map
    if (sharedMap) mat.map = null
    mat.dispose()
  }
}

function fixMeshMaterials (object) {
  object.traverse((child) => {
    if (!child.isMesh) return
    child.castShadow = true
    child.receiveShadow = true
    if (child.isSkinnedMesh) {
      child.frustumCulled = false
    }

    if (Array.isArray(child.material)) {
      child.material.forEach((mat, i) => replaceMaterial(child, mat, i))
    } else {
      replaceMaterial(child, child.material, 0)
    }
  })
}

function loadTextureFile (url) {
  const resolvedUrl = resolveStaticUrl(url)
  return new Promise((resolve, reject) => {
    const ext = (resolvedUrl.split('.').pop() || '').toLowerCase()
    const onLoad = (texture) => {
      if (THREE.sRGBEncoding) texture.encoding = THREE.sRGBEncoding
      texture.needsUpdate = true
      resolve(texture)
    }
    if (ext === 'tga' && TGALoader) {
      new TGALoader().load(resolvedUrl, onLoad, undefined, reject)
    } else {
      new THREE.TextureLoader().load(resolvedUrl, onLoad, undefined, reject)
    }
  })
}

/** RGB 图集无 alpha 时，把白底/浅黄底抠成透明（如向日葵叶片 billboard） */
function addCutoutAlphaFromBackground (texture, options) {
  const img = texture && texture.image
  if (!img || !img.width || !img.height) return texture
  const threshold = (options && options.cutoutThreshold) || 238
  const canvas = document.createElement('canvas')
  canvas.width = img.width
  canvas.height = img.height
  const ctx = canvas.getContext('2d')
  if (!ctx) return texture
  ctx.drawImage(img, 0, 0)
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
  const pixels = imageData.data
  for (let i = 0; i < pixels.length; i += 4) {
    const r = pixels[i]
    const g = pixels[i + 1]
    const b = pixels[i + 2]
    const isWhite = r >= threshold && g >= threshold && b >= threshold
    const isLightYellow = r >= 210 && g >= 195 && b >= 90 && b <= 215 && (r - b) > 25
    pixels[i + 3] = (isWhite || isLightYellow) ? 0 : 255
  }
  ctx.putImageData(imageData, 0, 0)
  if (texture.dispose) texture.dispose()
  const cutout = new THREE.CanvasTexture(canvas)
  if (THREE.sRGBEncoding) cutout.encoding = THREE.sRGBEncoding
  cutout.needsUpdate = true
  return cutout
}

function prepareDisplayTexture (texture, options) {
  if (!texture) return texture
  if (!options || !options.cutoutTexture) return texture
  if (options.alphaMap || options.skipBackgroundCutout) return texture
  if (textureImageHasAlpha(texture)) return texture
  return addCutoutAlphaFromBackground(texture, options)
}

function textureImageHasAlpha (texture) {
  const img = texture && texture.image
  if (!img || !img.width || !img.height) return false
  if (img instanceof HTMLCanvasElement) return true
  try {
    const canvas = document.createElement('canvas')
    canvas.width = img.width
    canvas.height = img.height
    const ctx = canvas.getContext('2d')
    if (!ctx) return false
    ctx.drawImage(img, 0, 0)
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data
    for (let i = 3; i < data.length; i += 16) {
      if (data[i] < 250) return true
    }
  } catch (err) {
    return false
  }
  return false
}

function cutoutMaterialFlags (options) {
  if (!options || (!options.cutoutTexture && !options.alphaMap)) return {}
  return {
    transparent: true,
    alphaTest: 0.08,
    depthWrite: true
  }
}

function disposeMeshResources (mesh) {
  if (!mesh) return
  if (mesh.geometry) mesh.geometry.dispose()
  if (mesh.material) {
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
    mats.forEach((mat) => {
      if (mat && mat.map) mat.map.dispose()
      if (mat) mat.dispose()
    })
  }
}

/** 合集 FBX 只保留指定网格 */
function pruneSandModelMeshes (object, keepNames) {
  if (!object || !keepNames || !keepNames.length) return
  const keep = new Set(keepNames)
  const removeList = []
  object.traverse((child) => {
    if (child.isMesh && !keep.has(child.name)) removeList.push(child)
  })
  removeList.forEach((mesh) => {
    if (mesh.parent) mesh.parent.remove(mesh)
    disposeMeshResources(mesh)
  })
}

/** 去掉指定网格（如爸爸和孩子里误绑马尾骨骼的 Object003 竖条） */
function removeSandModelMeshes (object, removeNames) {
  if (!object || !removeNames || !removeNames.length) return
  const remove = new Set(removeNames)
  const removeList = []
  object.traverse((child) => {
    if (child.isMesh && remove.has(child.name)) removeList.push(child)
  })
  removeList.forEach((mesh) => {
    if (mesh.parent) mesh.parent.remove(mesh)
    disposeMeshResources(mesh)
  })
}

function createLambertMaterialFromTexture (texture, skinning, options) {
  const opts = options || {}
  const polygon = {
    polygonOffset: true,
    polygonOffsetFactor: 1,
    polygonOffsetUnits: 4
  }
  const side = opts.side != null
    ? opts.side
    : (opts.cutoutTexture || opts.alphaMap ? THREE.DoubleSide : THREE.FrontSide)
  const cutout = cutoutMaterialFlags(opts)
  const alphaMap = opts.alphaMap || null
  // MeshBasicMaterial 不支持 skinning；骨骼网格必须用 Lambert 并开启 skinning
  if (opts.unlit && !skinning) {
    return new THREE.MeshBasicMaterial({
      map: texture,
      color: 0xffffff,
      side,
      fog: false,
      alphaMap,
      ...cutout,
      ...polygon
    })
  }
  if (opts.unlit && skinning) {
    return new THREE.MeshLambertMaterial({
      map: texture,
      emissive: 0xffffff,
      emissiveMap: texture,
      color: 0xffffff,
      skinning: true,
      side: THREE.DoubleSide,
      fog: false,
      alphaMap,
      ...cutout,
      ...polygon
    })
  }
  return new THREE.MeshLambertMaterial({
    map: texture,
    color: opts.color != null ? opts.color : 0xffffff,
    emissive: 0x000000,
    skinning: !!skinning,
    side,
    fog: false,
    alphaMap,
    ...cutout,
    ...polygon
  })
}

function replaceMaterialTexture (mat, texture, skinning, options) {
  if (mat && mat.map && mat.map !== texture) mat.map.dispose()
  if (mat) mat.dispose()
  return createLambertMaterialFromTexture(texture, skinning, options)
}

function applyTextureToMesh (mesh, texture, options) {
  const skinning = !!mesh.isSkinnedMesh
  if (skinning) {
    mesh.frustumCulled = false
  }
  mesh.castShadow = true
  mesh.receiveShadow = true
  const materialOptions = { unlit: true, ...(options || null) }
  if (Array.isArray(mesh.material)) {
    mesh.material.forEach((mat) => {
      if (mat && mat.map && mat.map !== texture) mat.map.dispose()
      if (mat) mat.dispose()
    })
    mesh.material = mesh.material.map(() =>
      createLambertMaterialFromTexture(texture, skinning, materialOptions)
    )
    return
  }
  mesh.material = replaceMaterialTexture(mesh.material, texture, skinning, materialOptions)
}

function snapshotFbxMaterialNames (object) {
  if (!object) return
  object.traverse((child) => {
    if (!child.isMesh) return
    const mats = Array.isArray(child.material) ? child.material : [child.material]
    child.userData._fbxMatNames = mats.map((mat) => (mat && mat.name) || '')
  })
}

/** 双图集：树干用 color，叶片 billboard 用 color + alphaMap（按 FBX 材质名区分） */
function applyModelDualAtlasTextures (object, colorUrl, alphaUrl) {
  if (!object || !colorUrl || !alphaUrl) return Promise.resolve()
  return Promise.all([
    loadTextureFile(colorUrl),
    loadTextureFile(alphaUrl)
  ]).then(([colorTex, alphaTex]) => {
    object.traverse((child) => {
      if (!child.isMesh) return
      const skinning = !!child.isSkinnedMesh
      const names = child.userData._fbxMatNames || []
      const mats = Array.isArray(child.material) ? child.material : [child.material]
      const nextMats = mats.map((mat, index) => {
        if (mat) mat.dispose()
        const matName = names[index] || (mat && mat.name) || ''
        const useAlpha = /alpha/i.test(matName)
        return createLambertMaterialFromTexture(colorTex, skinning, {
          cutoutTexture: useAlpha,
          alphaMap: useAlpha ? alphaTex : null,
          skipBackgroundCutout: useAlpha
        })
      })
      child.material = Array.isArray(child.material) ? nextMats : nextMats[0]
      delete child.userData._fbxMatNames
    })
  }).catch((err) => {
    console.warn('[sand prop] dual atlas textures failed:', colorUrl, alphaUrl, err)
  })
}

function applyModelTextureOverride (object, textureUrl, options) {
  if (!object || !textureUrl) return Promise.resolve()
  const loads = [loadTextureFile(textureUrl)]
  if (options && options.alphaTextureUrl) {
    loads.push(loadTextureFile(options.alphaTextureUrl))
  }
  return Promise.all(loads).then((textures) => {
    const colorTex = textures[0]
    const alphaTex = textures[1] || null
    const materialOptions = {
      ...(options || {}),
      alphaMap: alphaTex,
      skipBackgroundCutout: !!alphaTex
    }
    const displayTexture = prepareDisplayTexture(colorTex, materialOptions)
    object.traverse((child) => {
      if (!child.isMesh) return
      applyTextureToMesh(child, displayTexture, materialOptions)
    })
  }).catch((err) => {
    console.warn('[sand prop] texture override failed:', textureUrl, err)
  })
}

/** 按网格名贴图（如爸爸 n_ca069 / 孩子 wushi_02 各用不同 UV 图） */
function applyModelMeshTextures (object, meshTextures, cutoutMeshes) {
  if (!object || !meshTextures) return Promise.resolve()
  const entries = Object.entries(meshTextures).filter(([, url]) => url)
  if (!entries.length) return Promise.resolve()
  const cutoutSet = cutoutMeshes && cutoutMeshes.length ? new Set(cutoutMeshes) : null
  return Promise.all(
    entries.map(([meshName, url]) =>
      loadTextureFile(resolveStaticUrl(url)).then((texture) => ({ meshName, texture }))
    )
  ).then((items) => {
    items.forEach(({ meshName, texture }) => {
      const mesh = object.getObjectByName(meshName)
      if (!mesh || !mesh.isMesh) return
      const materialOptions = {
        unlit: true,
        ...(cutoutSet && cutoutSet.has(meshName) ? { cutoutTexture: true } : {})
      }
      applyTextureToMesh(mesh, prepareDisplayTexture(texture, materialOptions), materialOptions)
    })
  }).catch((err) => {
    console.warn('[sand prop] mesh textures failed:', meshTextures, err)
  })
}

/** 多材质网格按 slot 顺序贴图（如唐僧服装/脸/附件） */
function applyModelMaterialTextures (object, textureUrls, spec) {
  if (!object || !textureUrls || !textureUrls.length) return Promise.resolve()
  const skipMeshes = spec && spec.meshTextures
    ? new Set(Object.keys(spec.meshTextures))
    : null
  const urls = textureUrls.map((u) => resolveStaticUrl(u))
  const materialOptions = { unlit: true }
  return Promise.all(urls.map((u) => loadTextureFile(u))).then((textures) => {
    object.traverse((child) => {
      if (!child.isMesh) return
      if (skipMeshes && skipMeshes.has(child.name)) return
      const skinning = !!child.isSkinnedMesh
      const mats = Array.isArray(child.material)
        ? child.material
        : [child.material]
      if (mats.length <= 1) {
        child.material = replaceMaterialTexture(
          mats[0],
          textures[0],
          skinning,
          materialOptions
        )
        return
      }
      child.material = mats.map((mat, index) => {
        const tex = textures[index] || textures[textures.length - 1]
        return replaceMaterialTexture(mat, tex, skinning, materialOptions)
      })
    })
  }).catch((err) => {
    console.warn('[sand prop] material textures failed:', textureUrls, err)
  })
}

/** 鱼模型躺平后 y/footprint，越小越贴沙盘 */
function fishFlatScore (object) {
  object.updateMatrixWorld(true)
  const size = new THREE.Box3().setFromObject(object).getSize(new THREE.Vector3())
  const footprint = Math.max(size.x, size.z)
  if (footprint < 0.0001) return Infinity
  return size.y / footprint
}

function isButterflyShapedFishExport (object) {
  if (!object) return false
  object.updateMatrixWorld(true)
  const size = new THREE.Box3().setFromObject(object).getSize(new THREE.Vector3())
  return (
    size.x < size.y * 0.25 &&
    size.x < size.z * 0.25 &&
    Math.max(size.y, size.z) > 10
  )
}

/** 立体游鱼（manifest 或 dongwu 路径） */
function isVolumetricFishSpec (spec) {
  if (!spec) return false
  if (spec.fishDisplay === 'flat') return false
  if (spec.fishDisplay === 'volumetric') return true
  const ref = spec.url || spec.key || ''
  return /\/dongwu\/(huoshanyu|jianzuiyu|hudieyu)\//.test(ref)
}

function getVolumetricFishSlug (spec) {
  const ref = spec && (spec.url || spec.key || '')
  const match = ref.match(/\/dongwu\/([^/]+)\//)
  return match ? match[1] : null
}

/** 包围盒 Y 明显高于 XZ 跨度 → 鱼头朝天的竖直错误姿态 */
function isVolumetricFishVerticallyWrong (model) {
  if (!model) return false
  model.updateMatrixWorld(true)
  const size = new THREE.Box3().setFromObject(model).getSize(new THREE.Vector3())
  const footprint = Math.max(size.x, size.z)
  if (footprint < 0.0001) return false
  return size.y / footprint > 0.85
}

/** 火山鱼/剑嘴鱼：保留 FBX 导出 -90° X，与列表缩略图一致（水平平游、头朝 +X） */
function getVolumetricFishSwimRotation (spec, model) {
  if (spec && spec.modelRotation && spec.modelRotation.length === 3) {
    return spec.modelRotation
  }
  const slug = getVolumetricFishSlug(spec)
  if (slug === 'huoshanyu' || slug === 'jianzuiyu') {
    return [-Math.PI / 2, 0, 0]
  }
  if (isButterflyShapedFishExport(model)) {
    return [0, 0, Math.PI / 2]
  }
  return [0, 0, 0]
}

/** 立体游鱼：Y 应为最薄轴；仅在没有 slug 专用朝向时做 yaw 微调 */
function enforceVolumetricFishFlatSwim (model) {
  if (!model) return
  model.rotation.order = 'XYZ'
  model.rotation.set(0, 0, 0)
  model.updateMatrixWorld(true)
  let size = new THREE.Box3().setFromObject(model).getSize(new THREE.Vector3())
  let footprint = Math.max(size.x, size.z)
  if (footprint < 0.0001) return
  if (size.y / footprint < 0.55) return

  const yawCandidates = [Math.PI, Math.PI / 2, -Math.PI / 2]
  let bestYaw = 0
  let bestScore = size.y / footprint
  yawCandidates.forEach((ry) => {
    model.rotation.set(0, ry, 0)
    model.updateMatrixWorld(true)
    size = new THREE.Box3().setFromObject(model).getSize(new THREE.Vector3())
    footprint = Math.max(size.x, size.z)
    if (footprint < 0.0001) return
    const score = size.y / footprint
    if (score < bestScore) {
      bestScore = score
      bestYaw = ry
    }
  })
  model.rotation.set(0, bestYaw, 0)
  model.updateMatrixWorld(true)
}

/** 立体游鱼：纠正 Z 轴立起/鱼头朝天，恢复与缩略图一致的水平平游 */
export function fixVolumetricFishFacing (model, spec) {
  if (!model || !isVolumetricFishSpec(spec)) return
  flattenMeshMinus90X(model)
  model.rotation.order = 'XYZ'

  const swim = getVolumetricFishSwimRotation(spec, model)
  const rz = model.rotation.z
  const hasBadRoll = Math.abs(Math.abs(rz) - Math.PI / 2) <= 0.08
  if (hasBadRoll || isVolumetricFishVerticallyWrong(model)) {
    model.rotation.set(swim[0], swim[1], swim[2])
  } else if (spec.modelRotation && spec.modelRotation.length === 3) {
    model.rotation.set(swim[0], swim[1], swim[2])
  } else if (getVolumetricFishSlug(spec) === 'huoshanyu' || getVolumetricFishSlug(spec) === 'jianzuiyu') {
    model.rotation.set(swim[0], swim[1], swim[2])
  } else if (isButterflyShapedFishExport(model)) {
    model.rotation.set(swim[0], swim[1], swim[2])
  } else {
    enforceVolumetricFishFlatSwim(model)
  }

  model.updateMatrixWorld(true)
  if (isVolumetricFishVerticallyWrong(model)) {
    model.rotation.set(swim[0], swim[1], swim[2])
    model.updateMatrixWorld(true)
  }
  model.userData.sandVolumetricFishLocked = true
}

/** 贴地生物（蜘蛛/蛇）：禁止被 uprightModelIfLying 立起，保持趴沙盘 */
export function ensureLayFlatCritterPose (model, spec) {
  if (!model || !(spec && spec.layFlatOnSand)) return
  model.rotation.order = 'XYZ'
  const rx = model.rotation.x
  if (Math.abs(rx - Math.PI / 2) <= 0.08 || Math.abs(rx + Math.PI / 2) <= 0.08) {
    model.rotation.x = 0
  }
  if (spec.modelRotation && spec.modelRotation.length === 3) {
    model.rotation.set(
      spec.modelRotation[0],
      spec.modelRotation[1],
      spec.modelRotation[2]
    )
  }
  model.updateMatrixWorld(true)
  model.userData.sandLayFlatLocked = true
}

/** 立体游鱼归一化入口 */
function orientVolumetricSkinnedFish (object, spec) {
  fixVolumetricFishFacing(object, spec)
}

/**
 * flat 鱼躺平：蝴蝶鱼（X 极薄竖在 YZ）绕 Z 贴沙盘；骨骼鱼清除 FBX 根 -90° 并选最贴沙盘朝向。
 * @returns {boolean} 已躺平则不再对根节点 upright
 */
function orientFlatFishModel (object, spec) {
  if (!object || isVolumetricFishSpec(spec)) return false
  object.updateMatrixWorld(true)

  if (
    shouldLayFlatFish(spec) &&
    isButterflyShapedFishExport(object)
  ) {
    object.rotation.set(0, 0, Math.PI / 2)
    object.updateMatrixWorld(true)
    return true
  }

  if (!collectionHasSkinnedMesh(object)) return false
  const exportFlat = Math.abs(object.rotation.x + Math.PI / 2) <= 0.05
  const uprightFlat = Math.abs(object.rotation.x) <= 0.05
  if (!exportFlat && !uprightFlat) return false

  const candidates = [
    [0, 0, 0],
    [0, 0, Math.PI / 2],
    [0, 0, -Math.PI / 2],
    [0, Math.PI / 2, 0],
    [0, -Math.PI / 2, 0]
  ]
  let bestRot = candidates[0]
  let bestScore = Infinity
  candidates.forEach(([rx, ry, rz]) => {
    object.rotation.set(rx, ry, rz)
    const s = fishFlatScore(object)
    if (s < bestScore) {
      bestScore = s
      bestRot = [rx, ry, rz]
    }
  })
  object.rotation.set(bestRot[0], bestRot[1], bestRot[2])
  object.updateMatrixWorld(true)
  return bestScore < 0.55
}

/**
 * Z-up 躺平导出：Y 很薄且明显低于水平宽度（如霸王龙 y≈20、宽≈12 不应触发）。
 * 绕 X +90° 立起（等同旧版 mesh.rotation.x: -90° → 0）。
 */
function uprightModelIfLying (object) {
  if (!object) return
  if (object.userData && (object.userData.sandVolumetricFishLocked || object.userData.sandLayFlatLocked)) return
  object.updateMatrixWorld(true)
  const box = new THREE.Box3().setFromObject(object)
  const size = box.getSize(new THREE.Vector3())
  const footprint = Math.max(size.x, size.z)
  const minXZ = Math.min(size.x, size.z)
  if (
    footprint > 0.0001 &&
    size.y < footprint * 0.55 &&
    size.y < minXZ * 0.6
  ) {
    object.rotation.x += Math.PI / 2
    object.updateMatrixWorld(true)
  }
}

/** 静态 flat 鱼（比目鱼）：mesh -90° X 展平后薄轴常在 Z，需再翻到沙盘 XZ 平面 */
function layStaticFlatFishOnSand (object) {
  if (!object || collectionHasSkinnedMesh(object)) return
  object.updateMatrixWorld(true)
  const size = new THREE.Box3().setFromObject(object).getSize(new THREE.Vector3())
  if (size.y <= size.x * 0.45 && size.y <= size.z * 0.45) return
  if (size.z < size.x && size.z < size.y) {
    object.rotation.x += Math.PI / 2
  } else if (size.x < size.y && size.x < size.z) {
    object.rotation.z += Math.PI / 2
  }
  object.updateMatrixWorld(true)
}

function applyFlatFishDoubleSide (object) {
  if (!object) return
  object.traverse((child) => {
    if (!child.isMesh || !child.material) return
    const mats = Array.isArray(child.material) ? child.material : [child.material]
    mats.forEach((mat) => {
      if (mat) mat.side = THREE.DoubleSide
    })
  })
}

/** 网格带 -90° X（如比目鱼）：在父级坐标里躺平，避免再对根节点 upright 导致位移跑偏 */
function flattenMeshMinus90X (object) {
  if (!object) return
  const root = object.getObjectByName('PropPivot') || object
  const euler = new THREE.Euler()
  let changed = false
  root.traverse((child) => {
    if (!child.isMesh) return
    euler.setFromQuaternion(child.quaternion, 'XYZ')
    if (Math.abs(euler.x + Math.PI / 2) < 0.05) {
      euler.x = 0
      euler.y += Math.PI
      euler.z += Math.PI
      child.quaternion.setFromEuler(euler)
      changed = true
    }
  })
  if (changed) object.updateMatrixWorld(true)
}

function uprightCollectionModel (object) {
  flattenMeshMinus90X(object)
}

function isFangziModel (spec, object) {
  if (spec && spec.key && spec.key.indexOf('平房') !== -1) return true
  const ref = spec && (spec.url || spec.key || '')
  return ref.indexOf('fangzi_02.FBX') !== -1
}

/** 平房 FBX mesh 自带 -90° X，直接烘焙立起，避免 flattenMeshMinus90X 额外扭转 */
function orientFangziModel (object) {
  stripNonMeshChildren(object)
  object.position.set(0, 0, 0)
  object.rotation.set(0, 0, 0)
  object.scale.set(1, 1, 1)
  object.updateMatrixWorld(true)
  bakeRotationIntoGeometry(object)
}

function isTaoziModel (spec, object) {
  if (spec && spec.modelRotation && spec.modelRotation.length === 3) return false
  let hasTaoziMesh = false
  object.traverse((child) => {
    if (child.isMesh && child.name === 'taozi') hasTaoziMesh = true
  })
  if (hasTaoziMesh) return true
  if (!spec) return false
  const ref = spec.url || spec.key || ''
  return ref === TAOZI_MODEL_URL || ref.indexOf('桃子/taozi.FBX') !== -1
}

function stripNonMeshChildren (object) {
  if (!object) return
  const remove = []
  object.traverse((child) => {
    if (child !== object && !child.isMesh) remove.push(child)
  })
  remove.forEach((child) => {
    if (child.parent) child.parent.remove(child)
  })
}

/** 去掉 FBX 自带的灯、相机等，避免每放一个沙具场景就更亮 */
function stripFbxSceneArtifacts (object) {
  if (!object) return
  const remove = []
  object.traverse((child) => {
    if (child === object) return
    if (child.isLight || child.isCamera) remove.push(child)
  })
  remove.forEach((node) => {
    if (node.parent) node.parent.remove(node)
  })
}

function cloneMeshMaterials (root) {
  if (!root) return
  root.traverse((child) => {
    if (!child.isMesh || !child.material) return
    if (Array.isArray(child.material)) {
      child.material = child.material.map((m) => m.clone())
    } else {
      child.material = child.material.clone()
    }
  })
}

/** 把当前层级变换写入几何体顶点，根节点归零 */
function bakeRotationIntoGeometry (object) {
  if (!object) return
  object.updateMatrixWorld(true)
  object.traverse((child) => {
    if (!child.isMesh || !child.geometry) return
    const geo = child.geometry.clone()
    geo.applyMatrix4(child.matrixWorld)
    child.geometry.dispose()
    child.geometry = geo
    child.position.set(0, 0, 0)
    child.rotation.set(0, 0, 0)
    child.scale.set(1, 1, 1)
  })
  object.position.set(0, 0, 0)
  object.rotation.set(0, 0, 0)
  object.scale.set(1, 1, 1)
  object.updateMatrixWorld(true)
}

/** 桃子 FBX 俯视导出；展平 mesh 后绕 Z 立起，果尖朝 +Y */
function orientTaoziModel (object) {
  stripNonMeshChildren(object)
  flattenMeshMinus90X(object)
  object.position.set(0, 0, 0)
  object.rotation.set(0, 0, -Math.PI / 2)
  object.scale.set(1, 1, 1)
  object.updateMatrixWorld(true)
  bakeRotationIntoGeometry(object)
}

function isWitheredTreeModel (spec) {
  if (!spec) return false
  if (spec.modelRotation && spec.modelRotation.length === 3) return false
  const ref = spec.url || spec.key || ''
  return ref.indexOf('cj2_tree000.FBX') !== -1
}

/** 枯树 FBX 常倒着导出；按几何重心判断，必要时绕 X 180° 翻正再烘焙 */
function orientWitheredTreeModel (object) {
  stripNonMeshChildren(object)
  flattenMeshMinus90X(object)
  object.position.set(0, 0, 0)
  object.rotation.set(0, 0, 0)
  object.scale.set(1, 1, 1)
  object.updateMatrixWorld(true)

  const box = new THREE.Box3().setFromObject(object)
  if (!box.isEmpty()) {
    const midY = box.getCenter(new THREE.Vector3()).y
    let upper = 0
    let lower = 0
    const sample = new THREE.Vector3()
    object.traverse((child) => {
      if (!child.isMesh || !child.geometry || !child.geometry.attributes.position) return
      const pos = child.geometry.attributes.position
      const step = Math.max(1, Math.floor(pos.count / 240))
      for (let i = 0; i < pos.count; i += step) {
        sample.fromBufferAttribute(pos, i)
        sample.applyMatrix4(child.matrixWorld)
        if (sample.y >= midY) upper++
        else lower++
      }
    })
    if (upper > lower * 1.15) {
      object.rotation.x = Math.PI
      object.updateMatrixWorld(true)
    }
  }

  bakeRotationIntoGeometry(object)
}

/** 裁剪后把保留网格收到同一 pivot，去掉合集里的大位移父级 */
function compactKeptMeshesToPivot (object) {
  const meshes = []
  object.traverse((child) => {
    if (child.isMesh) meshes.push(child)
  })
  if (!meshes.length) return object

  object.updateMatrixWorld(true)
  const pivot = new THREE.Group()
  pivot.name = 'PropPivot'
  const wp = new THREE.Vector3()
  const wq = new THREE.Quaternion()
  const ws = new THREE.Vector3()

  meshes.forEach((mesh) => {
    mesh.matrixWorld.decompose(wp, wq, ws)
    if (mesh.parent) mesh.parent.remove(mesh)
    pivot.add(mesh)
    mesh.position.copy(wp)
    mesh.quaternion.copy(wq)
    mesh.scale.copy(ws)
  })

  while (object.children.length > 0) {
    object.remove(object.children[0])
  }
  object.add(pivot)
  object.updateMatrixWorld(true)
  return object
}

function getPrimaryMesh (object) {
  let best = null
  let bestVerts = 0
  object.traverse((child) => {
    if (!child.isMesh || !child.geometry) return
    const n = child.geometry.attributes.position
      ? child.geometry.attributes.position.count
      : 0
    if (n > bestVerts) {
      bestVerts = n
      best = child
    }
  })
  return best
}

/** 用几何体本地包围盒 × mesh 缩放，避免合集里世界坐标把尺寸算乱 */
function getMeshGeometryMaxDim (mesh) {
  const geo = mesh.geometry
  if (!geo) return 0
  if (!geo.boundingBox) geo.computeBoundingBox()
  if (!geo.boundingBox) return 0
  const size = new THREE.Vector3()
  geo.boundingBox.getSize(size)
  size.x *= Math.abs(mesh.scale.x)
  size.y *= Math.abs(mesh.scale.y)
  size.z *= Math.abs(mesh.scale.z)
  return Math.max(size.x, size.y, size.z)
}

/**
 * 缩放依据：主网格（顶点最多）的高度优先，多部件角色再取各部件本地尺寸最大值。
 */
function getModelScaleReferenceDim (object) {
  object.updateMatrixWorld(true)
  const worldBox = new THREE.Box3().setFromObject(object)
  const worldSize = worldBox.getSize(new THREE.Vector3())
  const worldHeight = worldSize.y
  const worldFootprint = Math.max(worldSize.x, worldSize.z)
  if (worldHeight > 0.0001) {
    return Math.max(worldHeight, worldFootprint * 0.85)
  }

  const primary = getPrimaryMesh(object)
  if (primary) {
    const geo = primary.geometry
    if (!geo.boundingBox) geo.computeBoundingBox()
    if (geo.boundingBox) {
      const size = new THREE.Vector3()
      geo.boundingBox.getSize(size)
      size.x *= Math.abs(primary.scale.x)
      size.y *= Math.abs(primary.scale.y)
      size.z *= Math.abs(primary.scale.z)
      const height = size.y
      const footprint = Math.max(size.x, size.z)
      if (height > 0.0001) {
        return Math.max(height, footprint * 0.85)
      }
    }
  }

  let ref = 0
  object.traverse((child) => {
    if (!child.isMesh) return
    ref = Math.max(ref, getMeshGeometryMaxDim(child))
  })
  if (ref > 0.0001) return ref

  const box = new THREE.Box3().setFromObject(object)
  const size = box.getSize(new THREE.Vector3())
  return Math.max(size.x, size.y, size.z)
}

/** 贴地飞禽/薄蒙皮：按 XZ 展幅缩放，避免 Y 极薄导致缩成看不见 */
function getLayFlatFootprintScaleDim (object) {
  object.updateMatrixWorld(true)
  const size = new THREE.Box3().setFromObject(object).getSize(new THREE.Vector3())
  return Math.max(Math.max(size.x, size.z), size.y * 2, 0.0001)
}

/** 合集里每个角色在场景中有偏移，几何中心对齐到原点，俯仰/左右才绕自身旋转 */
function centerSandModelAtOrigin (object) {
  if (!object) return
  object.updateMatrixWorld(true)
  const box = new THREE.Box3().setFromObject(object)
  if (box.isEmpty()) return
  const center = box.getCenter(new THREE.Vector3())
  const pivot = object.getObjectByName('PropPivot')
  if (pivot) {
    pivot.worldToLocal(center)
    pivot.traverse((child) => {
      if (!child.isMesh) return
      child.position.x -= center.x
      child.position.y -= center.y
      child.position.z -= center.z
    })
  } else {
    const inv = new THREE.Matrix4().copy(object.matrixWorld).invert()
    const localCenter = center.clone().applyMatrix4(inv)
    object.traverse((child) => {
      if (!child.isMesh) return
      child.position.x -= localCenter.x
      child.position.y -= localCenter.y
      child.position.z -= localCenter.z
    })
  }
  object.updateMatrixWorld(true)
}

function collectionHasSkinnedMesh (object) {
  let found = false
  object.traverse((child) => {
    if (child.isSkinnedMesh) found = true
  })
  return found
}

/** 贴图卡：FBX 无法加载时，用 PNG 躺平贴沙盘 */
function createFlatCutoutBillboardRoot () {
  const geo = new THREE.PlaneGeometry(1, 1)
  geo.rotateX(-Math.PI / 2)
  const mat = new THREE.MeshLambertMaterial({
    color: 0xffffff,
    transparent: true,
    alphaTest: 0.08,
    side: THREE.DoubleSide,
    depthWrite: true
  })
  const mesh = new THREE.Mesh(geo, mat)
  mesh.name = 'CutoutBillboard'
  mesh.frustumCulled = false
  return mesh
}

/** 直立贴图卡：底部贴沙盘，正面朝 +Z */
function createUprightCutoutBillboardRoot () {
  const geo = new THREE.PlaneGeometry(1, 1)
  geo.translate(0, 0.5, 0)
  const mat = new THREE.MeshLambertMaterial({
    color: 0xffffff,
    transparent: true,
    alphaTest: 0.08,
    side: THREE.DoubleSide,
    depthWrite: true
  })
  const mesh = new THREE.Mesh(geo, mat)
  mesh.name = 'CutoutBillboard'
  mesh.frustumCulled = false
  return mesh
}

function adjustBillboardCutoutAspect (object, texture, spec) {
  const img = texture && texture.image
  if (!object || !img || !img.width || !img.height) return
  const aspect = img.width / img.height
  if (!isFinite(aspect) || aspect <= 0) return
  if (spec && spec.billboardUpright) {
    object.scale.x *= aspect
  } else {
    object.scale.z *= aspect
  }
  object.updateMatrixWorld(true)
}

/** 静态展示：把当前蒙皮姿态烘焙进 Mesh，避免 clone/贴地时骨骼复位 */
function bakeSkinnedMeshesToStatic (object) {
  if (!object) return false
  object.updateMatrixWorld(true)
  const rootInverse = new THREE.Matrix4().copy(object.matrixWorld).invert()
  const replacements = []
  object.traverse((child) => {
    if (!child.isSkinnedMesh || !child.geometry || !child.skeleton) return
    const srcGeo = child.geometry
    const posAttr = srcGeo.attributes.position
    const skinIndex = srcGeo.attributes.skinIndex
    const skinWeight = srcGeo.attributes.skinWeight
    if (!posAttr || !skinIndex || !skinWeight) return
    child.skeleton.update()
    child.updateMatrixWorld(true)
    const boneMatrices = child.skeleton.boneMatrices
    const positions = new Float32Array(posAttr.count * 3)
    for (let i = 0; i < posAttr.count; i++) {
      _vertexScratch.set(0, 0, 0)
      for (let j = 0; j < 4; j++) {
        const w = getBufferAttributeComponent(skinWeight, i, j)
        if (w <= 0) continue
        const boneIdx = Math.floor(getBufferAttributeComponent(skinIndex, i, j))
        if (boneIdx < 0 || boneIdx >= boneMatrices.length / 16) continue
        _skinScratch.fromBufferAttribute(posAttr, i)
        _boneMatScratch.fromArray(boneMatrices, boneIdx * 16)
        _skinScratch.applyMatrix4(_boneMatScratch)
        _vertexScratch.addScaledVector(_skinScratch, w)
      }
      _vertexScratch.applyMatrix4(child.matrixWorld).applyMatrix4(rootInverse)
      positions[i * 3] = _vertexScratch.x
      positions[i * 3 + 1] = _vertexScratch.y
      positions[i * 3 + 2] = _vertexScratch.z
    }
    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    if (srcGeo.attributes.uv) {
      geometry.setAttribute('uv', srcGeo.attributes.uv.clone())
    }
    if (srcGeo.attributes.uv2) {
      geometry.setAttribute('uv2', srcGeo.attributes.uv2.clone())
    }
    geometry.computeVertexNormals()
    const mesh = new THREE.Mesh(geometry, child.material)
    mesh.name = child.name
    mesh.frustumCulled = false
    mesh.castShadow = child.castShadow
    mesh.receiveShadow = child.receiveShadow
    replacements.push({ mesh, skinned: child })
  })
  replacements.forEach(({ mesh, skinned }) => {
    object.add(mesh)
    if (skinned.parent) skinned.parent.remove(skinned)
    if (skinned.geometry) skinned.geometry.dispose()
  })
  object.traverse((child) => {
    if (child.isBone) child.visible = false
  })
  object.updateMatrixWorld(true)
  return replacements.length > 0
}

/** _MainRig 导出为举手 bind pose，沙盘静态展示需垂臂 */
function relaxMainRigArms (object) {
  if (!object) return
  const leftHuman = object.getObjectByName('Shoulder_L')
  const rightHuman = object.getObjectByName('Shoulder_R')
  if (leftHuman && rightHuman) {
    leftHuman.rotation.z -= Math.PI / 2
    rightHuman.rotation.z += Math.PI / 2
  } else {
    const leftArm = object.getObjectByName('LeftArm')
    const rightArm = object.getObjectByName('RightArm')
    const leftShoulder = object.getObjectByName('LeftShoulder')
    if (leftArm && rightArm && leftShoulder) {
      leftArm.rotation.z -= Math.PI / 2
      rightArm.rotation.z += Math.PI / 2
    }
  }
  object.traverse((child) => {
    if (child.isSkinnedMesh && child.skeleton) child.skeleton.update()
  })
  object.updateMatrixWorld(true)
}

/** 骨骼角色合集：只动根节点缩放/位移，不能改 mesh 局部坐标 */
function normalizeSkinnedCollectionRoot (object, targetHeight, spec) {
  object.updateMatrixWorld(true)
  let box = new THREE.Box3().setFromObject(object)
  const size = box.getSize(new THREE.Vector3())
  const maxDim = Math.max(size.x, size.y, size.z)
  if (maxDim <= 0.0001) {
    object.scale.set(0.01, 0.01, 0.01)
    return
  }
  object.scale.multiplyScalar(targetHeight / maxDim)
  object.updateMatrixWorld(true)
  box = new THREE.Box3().setFromObject(object)
  const center = box.getCenter(new THREE.Vector3())
  object.position.x -= center.x
  object.position.z -= center.z
  object.position.y -= getMinVertexYInParentSpace(object, 2400)
  if (!spec || !spec.staticPose) {
    relaxMainRigArms(object)
  }
  object.updateMatrixWorld(true)
}

function sampleSkinnedPose (object, clips) {
  sampleSkinnedPoseAtTime(object, clips, 0)
}

/** 按动画进度采样蒙皮姿态（0=起始，0.5=中间帧）；可选把 mixer 挂到 storeTarget 以免姿态丢失 */
function sampleSkinnedPoseAtTime (object, clips, timeRatio = 0, storeTarget, spec) {
  if (!object || !clips || !clips.length) return null
  const clip = pickSpecAnimationClip(clips, spec)
  if (!clip) return null
  const mixer = new THREE.AnimationMixer(object)
  const action = mixer.clipAction(clip)
  action.play()
  const t = Math.max(0, clip.duration * timeRatio)
  mixer.update(t)
  object.traverse((child) => {
    if (child.isSkinnedMesh && child.skeleton) child.skeleton.update()
  })
  // stopAllAction 会把骨骼打回 bind pose，导致所有男孩动作看起来一样
  action.paused = true
  if (storeTarget) {
    storeTarget.userData.animMixer = mixer
    storeTarget.userData.animAction = action
  }
  return { mixer, action, clip }
}

const _vertexScratch = new THREE.Vector3()
const _skinScratch = new THREE.Vector3()
const _boneMatScratch = new THREE.Matrix4()
const _spaceInvScratch = new THREE.Matrix4()

/** three@0.128 BufferAttribute 无 getComponent，需兼容读分量 */
function getBufferAttributeComponent (attr, index, component) {
  if (!attr) return 0
  if (typeof attr.getComponent === 'function') return attr.getComponent(index, component)
  if (!attr.array) return 0
  const itemSize = attr.itemSize || 1
  return attr.array[index * itemSize + component]
}

function setBufferAttributeComponent (attr, index, component, value) {
  if (!attr) return
  if (typeof attr.setComponent === 'function') {
    attr.setComponent(index, component, value)
    return
  }
  if (!attr.array) return
  const itemSize = attr.itemSize || 1
  attr.array[index * itemSize + component] = value
}

/** 在 reference 局部空间遍历网格顶点（蒙皮网格按当前骨骼姿态） */
function forEachMeshVertexInSpace (root, inverseWorldMatrix, visitor, options = {}) {
  if (!root || !inverseWorldMatrix || !visitor) return
  const maxSamples = options.maxSamples || 2400
  root.updateMatrixWorld(true)

  root.traverse((child) => {
    if (!child.isMesh || !child.geometry) return
    const geometry = child.geometry
    const posAttr = geometry.attributes.position
    if (!posAttr) return

    const isSkinned =
      child.isSkinnedMesh &&
      child.skeleton &&
      geometry.attributes.skinIndex &&
      geometry.attributes.skinWeight
    if (isSkinned) child.skeleton.update()

    const boneMatrices = isSkinned ? child.skeleton.boneMatrices : null
    const skinIndex = isSkinned ? geometry.attributes.skinIndex : null
    const skinWeight = isSkinned ? geometry.attributes.skinWeight : null
    const step = Math.max(1, Math.ceil(posAttr.count / maxSamples))

    for (let i = 0; i < posAttr.count; i += step) {
      if (isSkinned) {
        _vertexScratch.set(0, 0, 0)
        for (let j = 0; j < 4; j++) {
          const w = getBufferAttributeComponent(skinWeight, i, j)
          if (w <= 0) continue
          const boneIdx = Math.floor(getBufferAttributeComponent(skinIndex, i, j))
          if (boneIdx < 0 || boneIdx >= boneMatrices.length / 16) continue
          _skinScratch.fromBufferAttribute(posAttr, i)
          _boneMatScratch.fromArray(boneMatrices, boneIdx * 16)
          _skinScratch.applyMatrix4(_boneMatScratch)
          _vertexScratch.addScaledVector(_skinScratch, w)
        }
      } else {
        _vertexScratch.fromBufferAttribute(posAttr, i)
      }
      _vertexScratch.applyMatrix4(child.matrixWorld).applyMatrix4(inverseWorldMatrix)
      visitor(_vertexScratch, child, i)
    }
  })
}

/**
 * 在指定空间（inverseWorldMatrix）内求模型可见顶点最低 Y。
 * 骨骼网格按当前骨骼姿态计算，比静态 bbox 更贴脚底。
 */
function getMinVertexYInSpace (root, inverseWorldMatrix, options = {}) {
  if (!root || !inverseWorldMatrix) return 0
  let minY = Infinity
  forEachMeshVertexInSpace(root, inverseWorldMatrix, (v) => {
    if (v.y < minY) minY = v.y
  }, options)
  return minY === Infinity ? 0 : minY
}

/** 模型顶点在父节点局部空间中的最低 Y */
function getMinVertexYInParentSpace (object, maxSamples) {
  if (!object) return 0
  const parent = object.parent
  object.updateMatrixWorld(true)
  if (!parent) {
    _spaceInvScratch.identity()
  } else {
    parent.updateMatrixWorld(true)
    _spaceInvScratch.copy(parent.matrixWorld).invert()
  }
  return getMinVertexYInSpace(object, _spaceInvScratch, { maxSamples })
}

/** 测量贴地高度前，将骨骼姿态同步到沙具展示帧 */
function syncSandPropGroundPose (prop) {
  if (!prop) return
  const model = prop.userData.animModel
  if (!model) return
  if (prop.userData.sandAnimHold) {
    // 模板/克隆已烘焙姿态时，勿再 AnimationMixer 采样（失败会全打回待机 bind pose）
    if (prop.userData.sandPoseBaked) {
      model.traverse((child) => {
        if (child.isSkinnedMesh && child.skeleton) child.skeleton.update()
      })
      return
    }
    const holdRatio = prop.userData.sandAnimHoldRatio != null ? prop.userData.sandAnimHoldRatio : 0.5
    sampleSkinnedPoseAtTime(model, prop.userData.animClips, holdRatio, prop, prop.userData.sandModelSpec)
    prop.userData.sandPoseBaked = true
    return
  }
  const mixer = prop.userData.animMixer
  if (!mixer) return
  const action = prop.userData.animAction
  if (action) {
    action.reset()
    action.play()
    action.time = 0
  }
  mixer.update(0)
  model.traverse((child) => {
    if (child.isSkinnedMesh && child.skeleton) child.skeleton.update()
  })
}

/** 沙具在参考节点（如 sandboxRoot）局部空间中的底面 Y */
export function getSandPropBottomLocalY (prop, referenceRoot, maxSamples = 2400) {
  if (!prop || !referenceRoot) return 0
  syncSandPropGroundPose(prop)
  prop.updateMatrixWorld(true)
  referenceRoot.updateMatrixWorld(true)
  _spaceInvScratch.copy(referenceRoot.matrixWorld).invert()
  let hasSkinned = false
  prop.traverse((child) => {
    if (child.isSkinnedMesh) hasSkinned = true
  })
  const box = new THREE.Box3().setFromObject(prop)
  const boxCorners = [
    new THREE.Vector3(box.min.x, box.min.y, box.min.z),
    new THREE.Vector3(box.min.x, box.min.y, box.max.z),
    new THREE.Vector3(box.min.x, box.max.y, box.min.z),
    new THREE.Vector3(box.min.x, box.max.y, box.max.z),
    new THREE.Vector3(box.max.x, box.min.y, box.min.z),
    new THREE.Vector3(box.max.x, box.min.y, box.max.z),
    new THREE.Vector3(box.max.x, box.max.y, box.min.z),
    new THREE.Vector3(box.max.x, box.max.y, box.max.z)
  ]
  let bboxMin = Infinity
  boxCorners.forEach((corner) => {
    corner.applyMatrix4(_spaceInvScratch)
    if (corner.y < bboxMin) bboxMin = corner.y
  })
  let result = hasSkinned ? null : bboxMin
  if (hasSkinned) {
    const skinnedMin = getMinVertexYInSpace(prop, _spaceInvScratch, { maxSamples })
    // 采样值异常（NaN、无穷、或偏离包围盒太多）或骨骼计算有问题时，用 bboxMin
    const threshold = Math.min(0.03, Math.abs(bboxMin) * 0.15 + 0.005)
    if (!isFinite(skinnedMin) || Math.abs(skinnedMin - bboxMin) > threshold) {
      result = bboxMin
    } else {
      result = skinnedMin
    }
  }
  return result
}

/** 模型脚底 footprint 水平半径（只采样靠近底面一小层顶点的 XZ 分布） */
export function getSandPropFootprintRadius (prop, referenceRoot, maxSamples = 1200) {
  if (!prop || !referenceRoot) return 0.15
  syncSandPropGroundPose(prop)
  prop.updateMatrixWorld(true)
  referenceRoot.updateMatrixWorld(true)
  const box = new THREE.Box3().setFromObject(prop)
  const bottomY = getSandPropBottomLocalY(prop, referenceRoot, maxSamples)
  const threshold = bottomY + (box.max.y - box.min.y) * 0.08 // 只取底面往上8%高度以内的顶点
  let sumX = 0
  let sumZ = 0
  let count = 0
  let minX = Infinity
  let maxX = -Infinity
  let minZ = Infinity
  let maxZ = -Infinity
  prop.traverse((child) => {
    if (!child.isMesh || !child.geometry) return
    const pos = child.geometry.attributes.position
    if (!pos) return
    const local = new THREE.Vector3()
    for (let i = 0; i < pos.count; i++) {
      local.fromBufferAttribute(pos, i)
      child.localToWorld(local)
      referenceRoot.worldToLocal(local)
      if (local.y > threshold) continue
      sumX += local.x; sumZ += local.z; count++
      if (local.x < minX) minX = local.x
      if (local.x > maxX) maxX = local.x
      if (local.z < minZ) minZ = local.z
      if (local.z > maxZ) maxZ = local.z
    }
  })
  if (count < 4) {
    // fallback：直接用包围盒半径
    return Math.max(maxX - minX, maxZ - minZ) * 0.5 || 0.15
  }
  // 以质心为基准的 max 偏移
  const cx = sumX / count
  const cz = sumZ / count
  let maxDist = 0
  prop.traverse((child) => {
    if (!child.isMesh || !child.geometry) return
    const pos = child.geometry.attributes.position
    if (!pos) return
    const local = new THREE.Vector3()
    for (let i = 0; i < pos.count; i++) {
      local.fromBufferAttribute(pos, i)
      child.localToWorld(local)
      referenceRoot.worldToLocal(local)
      if (local.y > threshold) continue
      const d = Math.sqrt((local.x - cx) * (local.x - cx) + (local.z - cz) * (local.z - cz))
      if (d > maxDist) maxDist = d
    }
  })
  return Math.max(maxDist * 1.1, 0.12)
}

/** 所有网格顶点在 XZ 上的质心（bind pose；通用模型用此避免蒙皮 NaN 导致不可见） */
function getVertexCentroidXZ (object) {
  if (!object) return null
  object.updateMatrixWorld(true)
  const sum = new THREE.Vector3()
  const v = new THREE.Vector3()
  let count = 0
  object.traverse((child) => {
    if (!child.isMesh || !child.geometry) return
    const pos = child.geometry.attributes.position
    if (!pos) return
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i)
      v.applyMatrix4(child.matrixWorld)
      sum.x += v.x
      sum.z += v.z
      count++
    }
  })
  if (count < 1) return null
  sum.x /= count
  sum.z /= count
  return sum
}

/** 人物专用：蒙皮姿态 XZ 质心（alignHumanCharacterFacing 贴地用） */
function getSkinnedVertexCentroidXZ (object, options = {}) {
  if (!object) return null
  object.updateMatrixWorld(true)
  const parent = object.parent
  if (parent) {
    parent.updateMatrixWorld(true)
    _spaceInvScratch.copy(parent.matrixWorld).invert()
  } else {
    _spaceInvScratch.identity()
  }
  let sumX = 0
  let sumZ = 0
  let count = 0
  forEachMeshVertexInSpace(object, _spaceInvScratch, (v) => {
    if (!isFinite(v.x) || !isFinite(v.z)) return
    sumX += v.x
    sumZ += v.z
    count++
  }, options)
  if (count < 1) return null
  return { x: sumX / count, z: sumZ / count }
}

/** 缩放后 XZ 对齐视觉重心、底面落 Y=0（bind pose 质心；通用静态/已烘焙模型） */
function groundAndCenterModelRoot (object) {
  if (!object) return
  object.updateMatrixWorld(true)
  const centroidXZ = getVertexCentroidXZ(object)
  if (centroidXZ && isFinite(centroidXZ.x) && isFinite(centroidXZ.z)) {
    object.position.x -= centroidXZ.x
    object.position.z -= centroidXZ.z
  } else {
    const box = new THREE.Box3().setFromObject(object)
    if (!box.isEmpty()) {
      const center = box.getCenter(new THREE.Vector3())
      object.position.x -= center.x
      object.position.z -= center.z
    }
  }
  const minY = getMinVertexYInParentSpace(object, 2400)
  if (isFinite(minY)) object.position.y -= minY
  object.updateMatrixWorld(true)
}

/** 人物骨骼：优先 Head（正脸），否则脊柱/骨盆 */
function findHumanFacingBone (object) {
  let head = null
  let spine = null
  let hips = null
  object.traverse((child) => {
    if (!child.isSkinnedMesh || !child.skeleton) return
    child.skeleton.bones.forEach((bone) => {
      const name = bone.name || ''
      if (
        /^Head$/i.test(name) ||
        /^mixamorig:Head$/i.test(name) ||
        /^Bip001 Head$/i.test(name) ||
        /^Bip001_Head$/i.test(name)
      ) head = bone
      if (/^Bip001$/i.test(name) || /hips/i.test(name)) hips = bone
      if ((/^Spine$/i.test(name) || /^Bip001 Spine$/i.test(name)) && !/Spine\d/i.test(name)) spine = bone
      if (/^Bip001_Spine$/i.test(name)) spine = bone
    })
  })
  return head || spine || hips
}

function getBoneForwardYaw (bone) {
  if (!bone) return 0
  bone.updateMatrixWorld(true)
  // 3ds Max Biped / 多数行走 FBX：局部 +X 才是人物正面，+Z 会偏 90°
  _skinScratch.set(1, 0, 0).transformDirection(bone.matrixWorld)
  const horiz = Math.hypot(_skinScratch.x, _skinScratch.z)
  if (horiz < 0.15) {
    // Mixamo 等：+X 沿脊柱向上，正面为局部 +Z
    _skinScratch.set(0, 0, 1).transformDirection(bone.matrixWorld)
  }
  return Math.atan2(_skinScratch.x, _skinScratch.z)
}

function normalizeFacingDelta (delta) {
  let d = delta
  while (d > Math.PI) d -= Math.PI * 2
  while (d < -Math.PI) d += Math.PI * 2
  return d
}

/** 沙盘默认相机在 +Z 侧看向中心，人物应朝 +Z 面向相机 */
const FACE_CAMERA_YAW = 0

/** 按网格名设置纯色（缺 UV 贴图时近似缩略图配色） */
function applyModelMeshColors (object, meshColors) {
  if (!object || !meshColors) return
  Object.entries(meshColors).forEach(([meshName, hex]) => {
    const mesh = object.getObjectByName(meshName)
    if (!mesh || !mesh.isMesh || !hex) return
    const current = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material
    if (hasWorkingMap(current)) return
    const skinning = !!mesh.isSkinnedMesh
    const color = new THREE.Color(hex)
    const mat = new THREE.MeshLambertMaterial({
      color,
      skinning,
      side: THREE.FrontSide,
      fog: false,
      polygonOffset: true,
      polygonOffsetFactor: 1,
      polygonOffsetUnits: 4
    })
    if (Array.isArray(mesh.material)) {
      mesh.material.forEach((m) => { if (m) m.dispose() })
      mesh.material = mesh.material.map(() => mat.clone())
    } else {
      if (mesh.material) mesh.material.dispose()
      mesh.material = mat
    }
  })
}

/** 行走类人物：贴地后绕 Y 对齐正面（朝 -Z / 相机） */
function alignHumanCharacterFacing (object, clips) {
  if (!object) return
  sampleSkinnedPose(object, clips)
  object.updateMatrixWorld(true)
  const bone = findHumanFacingBone(object)
  if (!bone) return
  const delta = normalizeFacingDelta(FACE_CAMERA_YAW - getBoneForwardYaw(bone))
  if (Math.abs(delta) < 0.02) return
  object.rotation.y += delta
  object.updateMatrixWorld(true)
  const centroidXZ = getSkinnedVertexCentroidXZ(object)
  if (centroidXZ && isFinite(centroidXZ.x) && isFinite(centroidXZ.z)) {
    object.position.x -= centroidXZ.x
    object.position.z -= centroidXZ.z
  }
}

function getHumanCharacterScaleReferenceDim (object, spec) {
  if (spec && spec.scaleMesh) {
    const named = object.getObjectByName(spec.scaleMesh)
    if (named) {
      named.updateMatrixWorld(true)
      const size = new THREE.Box3().setFromObject(named).getSize(new THREE.Vector3())
      const ref = Math.max(size.y, Math.max(size.x, size.z) * 0.85)
      if (ref > 0.0001) return ref
    }
  }
  let primary = null
  let maxVerts = 0
  object.traverse((child) => {
    if (!child.isSkinnedMesh || !child.geometry) return
    const pos = child.geometry.attributes.position
    const n = pos ? pos.count : 0
    if (n > maxVerts) {
      maxVerts = n
      primary = child
    }
  })
  if (primary) {
    primary.updateMatrixWorld(true)
    const size = new THREE.Box3().setFromObject(primary).getSize(new THREE.Vector3())
    return Math.max(size.y, Math.max(size.x, size.z) * 0.85)
  }
  object.updateMatrixWorld(true)
  const size = new THREE.Box3().setFromObject(object).getSize(new THREE.Vector3())
  return Math.max(size.y, Math.max(size.x, size.z) * 0.85)
}

/** 静态孩子网格挂到骨骼（如妈妈和孩子 yd → 头部），行走时与妈妈同步 */
function findObjectBone (object, boneName) {
  if (!object || !boneName) return null
  let bone = null
  object.traverse((child) => {
    if (child.isBone && child.name === boneName) bone = child
  })
  return bone
}

function reparentObjectPreserveWorld (child, newParent) {
  if (!child || !newParent || child.parent === newParent) return
  child.updateMatrixWorld(true)
  newParent.updateMatrixWorld(true)
  _boneMatScratch.copy(child.matrixWorld)
  if (child.parent) child.parent.remove(child)
  newParent.add(child)
  newParent.updateMatrixWorld(true)
  child.matrix.copy(newParent.matrixWorld).invert().multiply(_boneMatScratch)
  child.matrix.decompose(child.position, child.quaternion, child.scale)
  child.updateMatrixWorld(true)
}

function attachStaticChildMeshes (object, attachMap) {
  if (!object || !attachMap) return
  Object.entries(attachMap).forEach(([meshName, boneName]) => {
    const mesh = object.getObjectByName(meshName)
    const bone = findObjectBone(object, boneName)
    if (!mesh || !bone || mesh.isSkinnedMesh) return
    mesh.frustumCulled = false
    reparentObjectPreserveWorld(mesh, bone)
  })
}

/** 按爸爸+孩子 FBX 中父子相对位置，把孩子对齐到妈妈身体 */
function alignImportedChildToBody (targetObject, childRoot, sourceObject, importSpec, ratio) {
  const alignMeshName = importSpec.alignMesh || 'wushi_02'
  const srcBody = sourceObject.getObjectByName(importSpec.scaleRefMesh || 'n_ca069')
  const srcChild = sourceObject.getObjectByName(alignMeshName)
  const tgtBody = targetObject.getObjectByName(importSpec.scaleTargetMesh || 'nv')
  if (!srcBody || !srcChild || !tgtBody) {
    targetObject.add(childRoot)
    return
  }

  sourceObject.updateMatrixWorld(true)
  targetObject.updateMatrixWorld(true)

  const srcBodyC = new THREE.Box3().setFromObject(srcBody).getCenter(new THREE.Vector3())
  const srcChildC = new THREE.Box3().setFromObject(srcChild).getCenter(new THREE.Vector3())
  const offset = srcChildC.sub(srcBodyC).multiplyScalar(ratio)
  const bodyCenter = new THREE.Box3().setFromObject(tgtBody).getCenter(new THREE.Vector3())
  const desiredCenter = bodyCenter.clone().add(offset)

  targetObject.add(childRoot)
  childRoot.position.set(0, 0, 0)
  childRoot.updateMatrixWorld(true)

  const childMesh = childRoot.getObjectByName(alignMeshName) || childRoot
  const childCenter = new THREE.Box3().setFromObject(childMesh).getCenter(new THREE.Vector3())
  childRoot.position.add(desiredCenter.sub(childCenter))
  childRoot.updateMatrixWorld(true)
}

/** 从另一 FBX 导入已蒙皮孩子网格（妈妈和孩子：woman 缺孩子，从 man_walkwithchild 补 wushi_02） */
function importChildSkinnedGroup (targetObject, sourceObject, importSpec) {
  if (!targetObject || !sourceObject || !importSpec) return
  if (importSpec.removeLocalMeshes && importSpec.removeLocalMeshes.length) {
    removeSandModelMeshes(targetObject, importSpec.removeLocalMeshes)
  }

  const childRoot = SkeletonUtils.clone(sourceObject)
  childRoot.name = 'SandImportedChild'
  childRoot.position.set(0, 0, 0)
  childRoot.rotation.set(0, 0, 0)
  childRoot.scale.set(1, 1, 1)

  if (importSpec.meshRemove && importSpec.meshRemove.length) {
    removeSandModelMeshes(childRoot, importSpec.meshRemove)
  }

  const scaleRefName = importSpec.scaleRefMesh || 'n_ca069'
  const scaleTargetName = importSpec.scaleTargetMesh || 'nv'
  const srcRef = sourceObject.getObjectByName(scaleRefName)
  const tgtRef = targetObject.getObjectByName(scaleTargetName)
  let ratio = 1
  if (srcRef && tgtRef) {
    srcRef.updateMatrixWorld(true)
    tgtRef.updateMatrixWorld(true)
    const srcSize = new THREE.Box3().setFromObject(srcRef).getSize(new THREE.Vector3())
    const tgtSize = new THREE.Box3().setFromObject(tgtRef).getSize(new THREE.Vector3())
    if (srcSize.y > 0.0001) {
      ratio = tgtSize.y / srcSize.y
      if (importSpec.scaleBoost && importSpec.scaleBoost > 0) {
        ratio *= importSpec.scaleBoost
      }
      childRoot.scale.multiplyScalar(ratio)
    }
  }

  if (importSpec.alignToBody) {
    alignImportedChildToBody(targetObject, childRoot, sourceObject, importSpec, ratio)
  } else {
    const bone = findObjectBone(targetObject, importSpec.attachBone || 'Bip001_L_UpperArm')
    const off = importSpec.attachOffset || [10, -15, 5]
    if (bone) {
      bone.add(childRoot)
      childRoot.position.set(off[0], off[1], off[2])
      if (importSpec.attachRotation && importSpec.attachRotation.length === 3) {
        childRoot.rotation.set(
          importSpec.attachRotation[0],
          importSpec.attachRotation[1],
          importSpec.attachRotation[2]
        )
      }
    } else {
      targetObject.add(childRoot)
    }
  }

  childRoot.traverse((child) => {
    if (child.isMesh) child.frustumCulled = false
  })
  targetObject.updateMatrixWorld(true)
}

/** 男孩/女生 FBX 里不参与 clip 的根骨骼配件（如 boy 的 Bone016 剑） */
function suppressAnimDetachedAccessory (object, meshName) {
  if (!object || !meshName) return
  const mesh = object.getObjectByName(meshName)
  if (!mesh || !mesh.isSkinnedMesh || !mesh.skeleton) return
  const boneIndex = mesh.skeleton.bones.findIndex((bone) => bone.name === 'Bone016')
  if (boneIndex < 0) return
  const skinIndex = mesh.geometry.attributes.skinIndex
  const skinWeight = mesh.geometry.attributes.skinWeight
  if (!skinIndex || !skinWeight) return
  for (let vi = 0; vi < skinIndex.count; vi++) {
    for (let slot = 0; slot < 4; slot++) {
      if (getBufferAttributeComponent(skinIndex, vi, slot) !== boneIndex) continue
      if (getBufferAttributeComponent(skinWeight, vi, slot) <= 0) continue
      setBufferAttributeComponent(skinWeight, vi, slot, 0)
    }
  }
  skinWeight.needsUpdate = true
  mesh.skeleton.update()
}

/** 蒙皮 card 贴图：跳过 fixMeshMaterials 占位材质，留给 applyModelTextureOverride */
function shouldSkipFixMeshMaterialsForCardOverride (spec, object) {
  if (!spec || !spec.texture || !collectionHasSkinnedMesh(object)) return false
  return !!(spec.skinnedUnlit || spec.fishDisplay)
}

/** 蒙皮 mesh 超大 scale 收到根节点，避免 WebGL 不渲染；仅处理 scale > 1，勿与 prepareAnimSkinnedMeshForRender 混用 */
function hoistSkinnedMeshScaleToRoot (object) {
  if (!object) return
  object.traverse((child) => {
    if (!child.isSkinnedMesh || !child.isMesh) return
    const sx = child.scale.x
    const sy = child.scale.y
    const sz = child.scale.z
    const meshScale = Math.max(Math.abs(sx), Math.abs(sy), Math.abs(sz))
    if (meshScale <= 1.01) return
    object.scale.multiplyScalar(meshScale)
    child.scale.set(
      sx < 0 ? -1 : 1,
      sy < 0 ? -1 : 1,
      sz < 0 ? -1 : 1
    )
  })
  object.updateMatrixWorld(true)
}

/** 动作角色 FBX：scale 在蒙皮 mesh 上会导致 WebGL 不渲染；只把 scale 收到根节点，保留 mesh 自带 -90° 朝向 */
function prepareAnimSkinnedMeshForRender (object) {
  if (!object) return
  object.traverse((child) => {
    if (!child.isSkinnedMesh || !child.isMesh) return
    const meshScale = Math.max(child.scale.x, child.scale.y, child.scale.z)
    if (meshScale > 1.01) {
      object.scale.multiplyScalar(meshScale)
      child.scale.set(1, 1, 1)
    }
  })
  object.updateMatrixWorld(true)
}

function fixAnimSkinnedMeshMaterials (object) {
  object.traverse((child) => {
    if (!child.isMesh) return
    child.castShadow = true
    child.receiveShadow = true
    child.frustumCulled = false
    const skinning = !!child.isSkinnedMesh
    const mat = new THREE.MeshLambertMaterial({
      color: 0xffcc99,
      skinning,
      side: THREE.DoubleSide,
      fog: false,
      polygonOffset: true,
      polygonOffsetFactor: 1,
      polygonOffsetUnits: 4
    })
    if (Array.isArray(child.material)) {
      child.material.forEach((m) => { if (m) m.dispose() })
      child.material = child.material.map(() => mat.clone())
    } else {
      if (child.material) child.material.dispose()
      child.material = mat
    }
  })
}

/** 男孩/女生：动画 FBX 无网格，网格在 boy.FBX / girl.FBX；中间帧定格 + 蒙皮顶点贴地 */
function normalizeAnimSkinnedModel (object, targetHeight, clips, spec) {
  const holdRatio = spec && spec.animHoldRatio != null ? spec.animHoldRatio : 0.5
  const meshName = (spec && spec.scaleMesh) || 'boy'
  if (spec && spec.modelRotation && spec.modelRotation.length === 3) {
    if (spec.flattenMeshBeforeRotate) {
      uprightSkinnedAnimalExport(object)
      flattenMeshMinus90X(object)
    }
    object.rotation.order = 'XYZ'
    object.rotation.set(
      spec.modelRotation[0],
      spec.modelRotation[1],
      spec.modelRotation[2]
    )
    object.updateMatrixWorld(true)
  }
  prepareAnimSkinnedMeshForRender(object)
  suppressAnimDetachedAccessory(object, meshName)
  sampleSkinnedPoseAtTime(object, clips, holdRatio, null, spec)
  fixAnimSkinnedMeshMaterials(object)
  object.updateMatrixWorld(true)
  const refMesh = object.getObjectByName(meshName)
  const measureRoot = refMesh || object
  measureRoot.updateMatrixWorld(true)
  const box = new THREE.Box3().setFromObject(measureRoot)
  const size = box.getSize(new THREE.Vector3())
  const ref = Math.max(size.y, Math.max(size.x, size.z) * 0.85)
  if (ref <= 0.0001) {
    object.scale.set(0.01, 0.01, 0.01)
    return
  }
  object.scale.multiplyScalar(targetHeight / ref)
  object.updateMatrixWorld(true)
  const centroidXZ = getSkinnedVertexCentroidXZ(object)
  if (centroidXZ && isFinite(centroidXZ.x) && isFinite(centroidXZ.z)) {
    object.position.x -= centroidXZ.x
    object.position.z -= centroidXZ.z
  }
  let minY = getMinVertexYInParentSpace(object, 2400)
  if (isFinite(minY)) object.position.y -= minY
  object.updateMatrixWorld(true)
  ensureHumanCharacterMeshVisible(object)
}

function normalizeHumanCharacterRoot (object, targetHeight, clips, spec) {
  const useStaticPose = !!(spec && spec.staticPose)
  if (!useStaticPose) sampleSkinnedPose(object, clips)
  if (spec && spec.staticChildAttach) {
    attachStaticChildMeshes(object, spec.staticChildAttach)
  }
  object.updateMatrixWorld(true)
  const refDim = getHumanCharacterScaleReferenceDim(object, spec)
  if (window.__DBG_HUMAN) {
    let skinnedInfo = []
    let meshInfo = []
    object.traverse((child) => {
      if (child.isSkinnedMesh) {
        const bb = new THREE.Box3().setFromObject(child)
        const sz = bb.getSize(new THREE.Vector3())
        skinnedInfo.push(child.name + ' sz=' + sz.x.toFixed(3) + ',' + sz.y.toFixed(3) + ',' + sz.z.toFixed(3) + ' verts=' + (child.geometry && child.geometry.attributes.position ? child.geometry.attributes.position.count : 0))
      } else if (child.isMesh) {
        const bb = new THREE.Box3().setFromObject(child)
        const sz = bb.getSize(new THREE.Vector3())
        meshInfo.push(child.name + ' sz=' + sz.x.toFixed(3) + ',' + sz.y.toFixed(3) + ',' + sz.z.toFixed(3) + ' verts=' + (child.geometry && child.geometry.attributes.position ? child.geometry.attributes.position.count : 0))
      }
    })
    const rootBB = new THREE.Box3().setFromObject(object)
    const rootSz = rootBB.getSize(new THREE.Vector3())
    console.log('[DBG.H] spec=' + (spec && spec.modelKey) + ' targetHeight=' + targetHeight + ' refDim=' + refDim.toFixed(4) + ' scaleMesh=' + (spec && spec.scaleMesh) + ' rootSz=' + rootSz.x.toFixed(3) + ',' + rootSz.y.toFixed(3) + ',' + rootSz.z.toFixed(3))
    console.log('[DBG.H]   skinned: ' + skinnedInfo.join(' | '))
    console.log('[DBG.H]   mesh:    ' + meshInfo.join(' | '))
  }
  if (refDim <= 0.0001) {
    object.scale.set(0.01, 0.01, 0.01)
    return
  }
  object.scale.multiplyScalar(targetHeight / refDim)
  groundAndCenterModelRoot(object)
  alignHumanCharacterFacing(object, clips)
  if (!useStaticPose) relaxMainRigArms(object)
  object.updateMatrixWorld(true)
  ensureHumanCharacterMeshVisible(object)
  boostHumanCharacterChildRenderOrder(object, spec)
}

/** 人物沙具：禁用视锥裁剪，避免蒙皮 bind bbox 不准导致不渲染 */
function ensureHumanCharacterMeshVisible (object) {
  if (!object) return
  object.visible = true
  object.traverse((child) => {
    if (!child.isMesh) return
    child.visible = true
    child.frustumCulled = false
  })
}

/** 父亲主体 scaleMesh 先画，孩子/附件略后画，避免 Z 冲突时只剩孩子 */
function boostHumanCharacterChildRenderOrder (object, spec) {
  if (!object || !spec || !spec.scaleMesh) return
  const bodyName = spec.scaleMesh
  object.traverse((child) => {
    if (!child.isMesh) return
    child.frustumCulled = false
    child.renderOrder = child.name === bodyName ? 0 : 1
  })
}

function isMultiPartBuildingSpec (spec) {
  return !!(spec && spec.modelParts && spec.modelParts.length > 1)
}

function shouldLayFlatFish (spec) {
  return !!(spec && spec.fishDisplay === 'flat')
}

function isFishSpec (spec) {
  return !!(spec && spec.fishDisplay)
}

/** 蒙皮动物贴地：用骨骼姿态顶点质心/底面 */
function groundSkinnedModelRoot (object, maxSamples = 2400) {
  if (!object) return
  object.updateMatrixWorld(true)
  const centroidXZ = getSkinnedVertexCentroidXZ(object, { maxSamples })
  if (centroidXZ && isFinite(centroidXZ.x) && isFinite(centroidXZ.z)) {
    object.position.x -= centroidXZ.x
    object.position.z -= centroidXZ.z
  } else {
    const box = new THREE.Box3().setFromObject(object)
    if (!box.isEmpty()) {
      const center = box.getCenter(new THREE.Vector3())
      object.position.x -= center.x
      object.position.z -= center.z
    }
  }
  object.updateMatrixWorld(true)
  const minY = getMinVertexYInParentSpace(object, maxSamples)
  const box = new THREE.Box3().setFromObject(object)
  const boxMinY = box.isEmpty() ? minY : box.min.y
  let groundY = minY
  if (
    !isFinite(minY) ||
    Math.abs(minY - boxMinY) > Math.max(0.08, Math.abs(boxMinY) * 2 + 0.05)
  ) {
    groundY = boxMinY
  }
  if (isFinite(groundY)) object.position.y -= groundY
  object.updateMatrixWorld(true)
}

/** 四足/鸟类等骨骼动物（非鱼）：不走鱼类躺平评分；manifest 指定 modelRotation 的走专用朝向 */
function isUprightSkinnedAnimal (spec, object) {
  if (spec && spec.modelRotation && spec.modelRotation.length === 3) return false
  if (spec && spec.layFlatOnSand) return false
  if (isVolumetricFishSpec(spec)) return false
  if (spec && spec.fishDisplay) return false
  if (spec && spec.humanCharacter) return false
  if (spec && spec.meshKeep && spec.meshKeep.length) return false
  return collectionHasSkinnedMesh(object)
}

/** FBX 根节点 -90° X 时立起，避免被 orientFlatFishModel 当成鱼躺平 */
function uprightSkinnedAnimalExport (object) {
  if (!object) return
  if (Math.abs(object.rotation.x + Math.PI / 2) <= 0.05) {
    object.rotation.x = 0
    object.updateMatrixWorld(true)
  } else if (Math.abs(object.rotation.x - Math.PI / 2) <= 0.05) {
    object.rotation.x = 0
    object.updateMatrixWorld(true)
  }
}

/** 贴地蒙皮生物（海鸥/鳄鱼占位等）：保持 XZ 躺平；仅用包围盒贴地，不用骨骼顶点（避免超大骨骼坐标拉成竖条） */
function normalizeLayFlatSkinnedCritter (object, targetHeight, clips, spec) {
  const useStaticPose = !!(spec && spec.staticPose)
  if (!useStaticPose) sampleSkinnedPose(object, clips)
  fixMeshMaterials(object)
  uprightSkinnedAnimalExport(object)
  if (spec && spec.modelRotation && spec.modelRotation.length === 3) {
    object.rotation.order = 'XYZ'
    object.rotation.set(
      spec.modelRotation[0],
      spec.modelRotation[1],
      spec.modelRotation[2]
    )
  } else {
    flattenMeshMinus90X(object)
  }
  object.userData.sandLayFlatLocked = true
  object.updateMatrixWorld(true)
  const refDim = getLayFlatFootprintScaleDim(object)
  if (refDim <= 0.0001) {
    object.scale.set(0.01, 0.01, 0.01)
    return
  }
  object.scale.multiplyScalar(targetHeight / refDim)
  object.traverse((child) => {
    if (child.isSkinnedMesh && child.skeleton) child.skeleton.update()
    if (child.isMesh) child.frustumCulled = false
  })
  object.updateMatrixWorld(true)
  groundAndCenterModelRoot(object)
  ensureLayFlatCritterPose(object, spec)
  object.updateMatrixWorld(true)
}

/** 骨骼动物：立姿缩放 + 按顶点底面贴地（与放置时 resetPose 一致） */
function normalizeSkinnedUprightCharacter (object, targetHeight, clips, spec) {
  if (spec && (spec.flattenSkinnedMesh || spec.humanCharacter)) {
    prepareAnimSkinnedMeshForRender(object)
  }
  const holdRatio = spec && spec.animHoldRatio != null ? spec.animHoldRatio : null
  const useStaticPose = !!(spec && spec.staticPose)

  if (holdRatio != null) {
    sampleSkinnedPoseAtTime(object, clips, holdRatio, null, spec)
  } else if (!useStaticPose) {
    sampleSkinnedPose(object, clips)
  }
  if (!(spec && spec.skinnedUnlit)) {
    relaxMainRigArms(object)
  } else if (useStaticPose) {
    relaxMainRigArms(object)
  }
  if (spec && spec.hoistSkinnedMeshScale) {
    hoistSkinnedMeshScaleToRoot(object)
  }

  fixMeshMaterials(object)
  uprightSkinnedAnimalExport(object)
  flattenMeshMinus90X(object)
  uprightModelIfLying(object)
  object.updateMatrixWorld(true)
  const box = new THREE.Box3().setFromObject(object)
  const size = box.getSize(new THREE.Vector3())
  const refDim = spec && spec.scaleByHeight
    ? Math.max(size.y, 0.0001)
    : Math.max(size.x, size.y, size.z)
  if (refDim <= 0.0001) {
    object.scale.set(0.01, 0.01, 0.01)
    return
  }
  object.scale.multiplyScalar(targetHeight / refDim)
  object.traverse((child) => {
    if (child.isSkinnedMesh && child.skeleton) child.skeleton.update()
  })
  if (holdRatio != null && !useStaticPose) {
    sampleSkinnedPoseAtTime(object, clips, holdRatio, null, spec)
    object.traverse((child) => {
      if (child.isSkinnedMesh && child.skeleton) child.skeleton.update()
    })
  }
  groundAndCenterModelRoot(object)
  object.updateMatrixWorld(true)
}

/** 多 FBX 大厦：跳过鱼类/躺平自动朝向，仅按包围盒缩放并贴地 */
function normalizeBuildingModelRoot (object, targetHeight) {
  object.updateMatrixWorld(true)
  const box = new THREE.Box3().setFromObject(object)
  const size = box.getSize(new THREE.Vector3())
  const refDim = Math.max(size.y, Math.max(size.x, size.z) * 0.85)
  if (refDim > 0.0001) {
    object.scale.multiplyScalar(targetHeight / refDim)
    groundAndCenterModelRoot(object)
  } else {
    object.scale.set(0.01, 0.01, 0.01)
  }
  object.updateMatrixWorld(true)
}

function normalizeModel (object, targetHeight = 0.62, clips, spec) {
  if (spec && spec.billboardCutout) {
    object.updateMatrixWorld(true)
    const box = new THREE.Box3().setFromObject(object)
    const size = box.getSize(new THREE.Vector3())
    const refDim = Math.max(size.x, size.y, size.z, 0.0001)
    object.scale.multiplyScalar(targetHeight / refDim)
    groundAndCenterModelRoot(object)
    object.updateMatrixWorld(true)
    return
  }
  if (isVolumetricFishSpec(spec) && collectionHasSkinnedMesh(object)) {
    object.userData.sandVolumetricFishLocked = true
  }
  if (spec && spec.layFlatOnSand) {
    object.userData.sandLayFlatLocked = true
  }
  if (!(spec && spec.staticPose) && !(spec && spec.flattenSkinnedMesh) && !(spec && spec.animHoldRatio != null)) {
    sampleSkinnedPose(object, clips)
  }
  const hasMaterialTextures = !!(spec && spec.materialTextures && spec.materialTextures.length)
  const hasMeshSurfaceSpec = !!(spec && (spec.meshColors || spec.meshTextures))
  const isObjModel = !!(spec && spec.url && isObjModelUrl(spec.url))
  if (!hasMaterialTextures && !hasMeshSurfaceSpec && !isObjModel) {
    if (!(spec && shouldSkipFixMeshMaterialsForCardOverride(spec, object))) {
      fixMeshMaterials(object)
    }
    if (isFishSpec(spec)) applyFlatFishDoubleSide(object)
  }
  if (object.userData.sandSkinnedCollection) {
    normalizeSkinnedCollectionRoot(object, targetHeight, spec)
    if (spec && spec.staticPose) {
      object.position.y -= getMinVertexYInParentSpace(object, 2400)
      object.updateMatrixWorld(true)
    }
    return
  }
  if (isMultiPartBuildingSpec(spec)) {
    normalizeBuildingModelRoot(object, targetHeight)
    return
  }
  if (spec && spec.layFlatOnSand && collectionHasSkinnedMesh(object)) {
    normalizeLayFlatSkinnedCritter(object, targetHeight, clips, spec)
    return
  }
  if (isUprightSkinnedAnimal(spec, object)) {
    normalizeSkinnedUprightCharacter(object, targetHeight, clips, spec)
    return
  }
  if (spec && spec.flattenSkinnedMesh) {
    normalizeAnimSkinnedModel(object, targetHeight, clips, spec)
    return
  }
  if (spec && spec.humanCharacter) {
    normalizeHumanCharacterRoot(object, targetHeight, clips, spec)
    return
  }
  if (isTaoziModel(spec, object)) {
    orientTaoziModel(object)
  } else if (isWitheredTreeModel(spec)) {
    orientWitheredTreeModel(object)
  } else if (isFangziModel(spec, object)) {
    orientFangziModel(object)
  } else if (!object.getObjectByName('PropPivot')) {
    if (spec && spec.modelRotation && spec.modelRotation.length === 3) {
      if (isVolumetricFishSpec(spec) || spec.flattenMeshBeforeRotate) {
        uprightSkinnedAnimalExport(object)
      }
      if (spec.flattenMeshBeforeRotate) {
        flattenMeshMinus90X(object)
      }
      object.rotation.order = 'XYZ'
      object.rotation.set(
        spec.modelRotation[0],
        spec.modelRotation[1],
        spec.modelRotation[2]
      )
      object.updateMatrixWorld(true)
    } else if (isFishSpec(spec)) {
      if (collectionHasSkinnedMesh(object)) {
        if (shouldLayFlatFish(spec)) {
          uprightSkinnedAnimalExport(object)
          flattenMeshMinus90X(object)
          orientFlatFishModel(object, spec)
        } else {
          orientVolumetricSkinnedFish(object, spec)
        }
        if (shouldLayFlatFish(spec)) layStaticFlatFishOnSand(object)
      } else {
        const fishLaidFlat = orientFlatFishModel(object, spec)
        flattenMeshMinus90X(object)
        if (shouldLayFlatFish(spec)) layStaticFlatFishOnSand(object)
        if (!fishLaidFlat) uprightModelIfLying(object)
      }
    } else {
      const fishLaidFlat = shouldLayFlatFish(spec) ? orientFlatFishModel(object, spec) : false
      if (!fishLaidFlat && collectionHasSkinnedMesh(object) && !(spec && spec.humanCharacter) && !isFishSpec(spec)) {
        uprightSkinnedAnimalExport(object)
      }
      flattenMeshMinus90X(object)
      if (shouldLayFlatFish(spec)) layStaticFlatFishOnSand(object)
      if (!fishLaidFlat && !isFishSpec(spec) && !(spec && spec.layFlatOnSand)) {
        uprightModelIfLying(object)
      }
    }
  }
  const refDim = (spec && spec.forceRefDim) ? spec.forceRefDim : getModelScaleReferenceDim(object)
  if (window.__DBG_MODEL) {
    const box = new THREE.Box3().setFromObject(object)
    const sz = box.getSize(new THREE.Vector3())
    let skinnedNames = []
    object.traverse((c) => {
      if (c.isSkinnedMesh) {
        const bb = new THREE.Box3().setFromObject(c)
        const ss = bb.getSize(new THREE.Vector3())
        skinnedNames.push(c.name + ' sz=' + ss.x.toFixed(3) + ',' + ss.y.toFixed(3) + ',' + ss.z.toFixed(3))
      }
    })
    const msg = '[DBG.M] ' + (spec && spec.modelKey) + ' targetHeight=' + targetHeight + ' refDim=' + refDim.toFixed(4) + ' forced=' + (spec && spec.forceRefDim) + ' rootSz=' + sz.x.toFixed(3) + ',' + sz.y.toFixed(3) + ',' + sz.z.toFixed(3) + ' skinned=[' + skinnedNames.join(' | ') + ']'
    console.log(msg)
    let d = document.getElementById('__dbg_scale_div')
    if (!d) {
      d = document.createElement('div')
      d.id = '__dbg_scale_div'
      d.style.cssText = 'position:fixed;left:8px;bottom:8px;z-index:99999;background:#000;color:#0f0;font:11px monospace;padding:6px 10px;max-width:90vw;max-height:60vh;overflow:auto;white-space:pre;border-radius:6px;'
      document.body.appendChild(d)
    }
    d.textContent = msg + '\n' + (d.dataset.prev || '')
    d.dataset.prev = d.textContent
  }
  if (refDim > 0.0001) {
    object.scale.multiplyScalar(targetHeight / refDim)
    if ((isFishSpec(spec) || (spec && spec.layFlatOnSand)) && collectionHasSkinnedMesh(object)) {
      object.traverse((child) => {
        if (child.isSkinnedMesh && child.skeleton) child.skeleton.update()
      })
      groundSkinnedModelRoot(object)
    } else {
      groundAndCenterModelRoot(object)
    }
  } else {
    object.scale.set(0.01, 0.01, 0.01)
  }
  if (spec && spec.modelRotation && spec.modelRotation.length === 3) {
    if (!collectionHasSkinnedMesh(object)) {
      bakeRotationIntoGeometry(object)
    }
  }
  if (isVolumetricFishSpec(spec) && collectionHasSkinnedMesh(object)) {
    fixVolumetricFishFacing(object, spec)
  }
  if (spec && spec.layFlatOnSand && collectionHasSkinnedMesh(object)) {
    ensureLayFlatCritterPose(object, spec)
  }
  object.updateMatrixWorld(true)
}

function resolveTargetHeight (spec) {
  let height = spec.targetHeight
  if (height == null) {
    height = spec.meshKeep && spec.meshKeep.length ? 0.95 : 0.62
  }
  if (spec.meshKeep && spec.meshKeep.length > 1) {
    height *= 1.25
  }
  if (spec.scaleBoost && spec.scaleBoost > 0) {
    height *= spec.scaleBoost
  }
  return height
}

function countMeshes (object) {
  let n = 0
  if (!object) return n
  object.traverse((child) => {
    if (child.isMesh) n++
  })
  return n
}

function wrapSandModel (object) {
  const root = new THREE.Group()
  root.name = 'SandPropModel'
  root.add(object)
  root.userData.sandPropKind = 'model'
  return root
}

function bindModelAnimation (wrapper, model, clips, options) {
  const opts = options || {}
  const staticPose = opts.staticPose
  const holdRatio = opts.animHoldRatio
  const spec = opts.spec || null
  wrapper.userData.animClips = clips || []
  wrapper.userData.animModel = model
  wrapper.userData.sandModelSpec = spec
  wrapper.userData.animMixer = null
  wrapper.userData.animAction = null
  wrapper.userData.sandStaticPose = !!staticPose
  wrapper.userData.sandAnimHold = false
  wrapper.userData.sandAnimHoldRatio = null
  if (staticPose) {
    wrapper.userData.sandPoseBaked = true
    return
  }

  const clip = pickSpecAnimationClip(clips, spec)
  if (!clip) {
    if (process.env.NODE_ENV === 'development' && spec && spec.flattenSkinnedMesh) {
      console.warn('[sand prop] anim character clip missing:', spec.key || spec.url)
    }
    return
  }

  if (holdRatio != null) {
    sampleSkinnedPoseAtTime(model, clips, holdRatio, wrapper, spec)
    wrapper.userData.sandAnimHold = true
    wrapper.userData.sandAnimHoldRatio = holdRatio
    wrapper.userData.animClipName = clip.name
    wrapper.userData.sandPoseBaked = true
    return
  }

  const mixer = new THREE.AnimationMixer(model)
  const action = mixer.clipAction(clip)
  action.setLoop(THREE.LoopRepeat, Infinity)
  action.play()
  wrapper.userData.animMixer = mixer
  wrapper.userData.animAction = action
  wrapper.userData.animClipName = clip.name
}

/** 放置前将骨骼动画复位到第 0 帧，与归一化贴地姿态一致 */
export function resetSandPropPose (prop) {
  syncSandPropGroundPose(prop)
  const model = prop.userData.animModel
  const spec =
    prop.userData.sandModelSpec ||
    getSandModelSpec(
      prop.userData.sandModelUrl ||
        (prop.userData.sandProp && (prop.userData.sandProp.modelKey || prop.userData.sandProp.modelUrl)) ||
        ''
    )
  if (model && spec) fixVolumetricFishFacing(model, spec)
  if (model && spec) ensureLayFlatCritterPose(model, spec)
}

function isObjModelUrl (url) {
  return /\.obj(?:\?|#|$)/i.test(url || '')
}

function loadRawObj (url) {
  const modelUrl = resolveStaticUrl(url)
  if (!/\.obj(?:\?|#|$)/i.test(modelUrl)) throw new Error('not an obj url: ' + modelUrl)
  const cacheKey = rawFbxCacheKey(modelUrl, 'OBJ')
  if (rawFbxCache[cacheKey]) return Promise.resolve(rawFbxCache[cacheKey])
  if (rawFbxLoading[cacheKey]) return rawFbxLoading[cacheKey]

  rawFbxLoading[cacheKey] = fetch(modelUrl)
    .then((res) => {
      if (!res.ok) throw new Error('obj fetch failed: ' + modelUrl)
      return res.text()
    })
    .then((text) => {
      if (!OBJLoader) throw new Error('OBJLoader not available')
      const object = new OBJLoader().parse(text)
      // OBJ 自带材质（如 MeshPhongMaterial）可能阻碍后续 toDisplayMaterial/fallback；
      // 在加载后立即把材质统一收尾，确保 spec.texture 能通过 applyModelTextureOverride 接管
      object.traverse((child) => {
        if (!child.isMesh) return
        if (Array.isArray(child.material)) {
          child.material = child.material.map(() => new THREE.MeshLambertMaterial({ color: 0xffffff }))
        } else {
          child.material = new THREE.MeshLambertMaterial({ color: 0xffffff })
        }
      })

      const payload = { object, clips: [] }
      rawFbxCache[cacheKey] = payload
      delete rawFbxLoading[cacheKey]
      return payload
    })
    .catch((err) => {
      delete rawFbxLoading[cacheKey]
      throw err
    })

  return rawFbxLoading[cacheKey]
}

function loadRawFbx (url, textureRedirect) {
  const modelUrl = resolveStaticUrl(url)
  if (isObjModelUrl(modelUrl)) return loadRawObj(url)

  const redirectUrl = textureRedirect ? resolveStaticUrl(textureRedirect) : null
  const cacheKey = rawFbxCacheKey(modelUrl, redirectUrl)
  if (rawFbxCache[cacheKey]) return Promise.resolve(rawFbxCache[cacheKey])
  if (rawFbxLoading[cacheKey]) return rawFbxLoading[cacheKey]

  rawFbxLoading[cacheKey] = new Promise((resolve, reject) => {
    let settled = false

    function finishLoad (object) {
      if (settled || !object) return
      settled = true
      const payload = { object, clips: object.animations || [] }
      rawFbxCache[cacheKey] = payload
      delete rawFbxLoading[cacheKey]
      resolve(payload)
    }

    const manager = new THREE.LoadingManager()

    createFbxLoader(modelUrl, manager).load(
      modelUrl,
      (object) => finishLoad(object),
      undefined,
      (err) => {
        delete rawFbxLoading[cacheKey]
        reject(err)
      }
    )
  })

  return rawFbxLoading[cacheKey]
}

function loadMultiPartRawFbx (urls, textureRedirect, partOffsets) {
  const partUrls = (urls || []).filter(Boolean)
  if (!partUrls.length) return Promise.reject(new Error('empty modelParts'))
  if (partUrls.length === 1) return loadRawFbx(partUrls[0], textureRedirect)

  const resolvedParts = partUrls.map((u) => resolveStaticUrl(u))
  const redirectUrl = textureRedirect ? resolveStaticUrl(textureRedirect) : null
  const cacheKey = rawFbxCacheKey(
    'multipart:' + resolvedParts.join('|') + ':' + JSON.stringify(partOffsets || []),
    redirectUrl
  )
  if (rawFbxCache[cacheKey]) return Promise.resolve(rawFbxCache[cacheKey])
  if (rawFbxLoading[cacheKey]) return rawFbxLoading[cacheKey]

  rawFbxLoading[cacheKey] = Promise.all(
    partUrls.map((url) => loadRawFbx(url, textureRedirect))
  ).then((parts) => {
    const group = new THREE.Group()
    const clips = []
    parts.forEach((raw, index) => {
      const part = SkeletonUtils.clone(raw.object)
      stripFbxSceneArtifacts(part)
      bakeRotationIntoGeometry(part)
      const off = partOffsets && partOffsets[index]
      if (off && off.length >= 3) {
        part.position.set(off[0] || 0, off[1] || 0, off[2] || 0)
      }
      group.add(part)
      if (raw.clips && raw.clips.length) clips.push(...raw.clips)
    })
    group.updateMatrixWorld(true)
    const payload = { object: group, clips }
    rawFbxCache[cacheKey] = payload
    delete rawFbxLoading[cacheKey]
    return payload
  }).catch((err) => {
    delete rawFbxLoading[cacheKey]
    throw err
  })

  return rawFbxLoading[cacheKey]
}

function pickRawAnimationClips (raw) {
  if (!raw) return []
  if (raw.clips && raw.clips.length) return raw.clips
  return raw.object && raw.object.animations ? raw.object.animations : []
}

function loadSandModelRaw (spec) {
  if (spec.billboardCutout) {
    return Promise.resolve({
      object: spec.billboardUpright
        ? createUprightCutoutBillboardRoot()
        : createFlatCutoutBillboardRoot(),
      clips: []
    })
  }
  if (spec.modelParts && spec.modelParts.length > 1) {
    return loadMultiPartRawFbx(spec.modelParts, spec.texture || null, spec.modelPartOffsets)
  }
  if (spec.flattenSkinnedMesh && spec.meshSourceUrl) {
    const meshTex = spec.meshLoadTexture || null
    return Promise.all([
      loadRawFbx(spec.url, null),
      loadRawFbx(spec.meshSourceUrl, meshTex)
    ]).then(([animRaw, meshRaw]) => {
      const clips = pickRawAnimationClips(animRaw).map((c) => c.clone())
      if (!clips.length && process.env.NODE_ENV === 'development') {
        console.warn('[sand prop] anim character clips empty:', spec.key || spec.url)
      }
      return {
        object: meshRaw.object,
        clips
      }
    })
  }
  const textureRedirect = spec.texture || null
  return loadRawFbx(spec.url, textureRedirect).then((raw) => {
    if (countMeshes(raw.object) > 0) return raw
    const meshSource = spec.meshSourceUrl
    if (!meshSource) return raw
    const meshTextureRedirect = spec.meshLoadTexture || textureRedirect
    return loadRawFbx(meshSource, meshTextureRedirect).then((meshRaw) => ({
      object: meshRaw.object,
      clips: pickRawAnimationClips(raw)
    }))
  })
}

function buildSandModelVariant (spec) {
  const cacheKey = variantCacheKey(spec)
  if (variantTemplateCache[cacheKey]) return Promise.resolve(variantTemplateCache[cacheKey])
  if (variantLoading[cacheKey]) return variantLoading[cacheKey]

  variantLoading[cacheKey] = loadSandModelRaw(spec).then((raw) => {
    const parsedObject = SkeletonUtils.clone(raw.object)
    const parsedClips = (raw.clips || []).map((clip) => clip.clone())
    stripFbxSceneArtifacts(parsedObject)

    if (spec.meshKeep && spec.meshKeep.length) {
      pruneSandModelMeshes(parsedObject, spec.meshKeep)
      if (collectionHasSkinnedMesh(parsedObject)) {
        parsedObject.userData.sandSkinnedCollection = true
      } else {
        compactKeptMeshesToPivot(parsedObject)
        uprightCollectionModel(parsedObject)
        centerSandModelAtOrigin(parsedObject)
      }
    } else if (spec.meshRemove && spec.meshRemove.length) {
      removeSandModelMeshes(parsedObject, spec.meshRemove)
    }

    const importChild = spec.childMeshImport
      ? loadRawFbx(resolveStaticUrl(spec.childMeshImport.url)).then((childRaw) => {
        importChildSkinnedGroup(parsedObject, childRaw.object, spec.childMeshImport)
      })
      : Promise.resolve()

    return importChild.then(() => {
      if (spec.materialTextures && spec.materialTextures.length) {
        return applyModelMaterialTextures(parsedObject, spec.materialTextures, spec)
      }
    }).then(() => {
      if (spec.alphaTextureUrl && spec.texture) {
        snapshotFbxMaterialNames(parsedObject)
      }
      normalizeModel(parsedObject, resolveTargetHeight(spec), parsedClips, spec)
      if (spec.bakeSkinnedToStatic && collectionHasSkinnedMesh(parsedObject)) {
        sampleSkinnedPoseAtTime(parsedObject, parsedClips, 0, null, spec)
        bakeSkinnedMeshesToStatic(parsedObject)
      }
      const displayTextureUrl = (spec.billboardCutout && spec.thumbUrl)
        ? spec.thumbUrl
        : spec.texture
      if (displayTextureUrl && !spec.keepFbxTexture && !(spec.materialTextures && spec.materialTextures.length)) {
        if (spec.alphaTextureUrl && spec.texture) {
          return applyModelDualAtlasTextures(parsedObject, spec.texture, spec.alphaTextureUrl)
        }
        const needsUnlit = !spec.cutoutTexture && !spec.keepFbxTexture && (
          spec.skinnedUnlit ||
          spec.bakeSkinnedToStatic ||
          spec.flattenSkinnedMesh ||
          spec.fishDisplay ||
          spec.layFlatOnSand ||
          spec.key === 'dongwu:daxingxing' ||
          !!displayTextureUrl
        )
        const texOpts = {
          ...(spec.cutoutTexture ? { cutoutTexture: true } : {}),
          ...(spec.textureTintColor != null ? { color: spec.textureTintColor } : {}),
          ...(needsUnlit ? { side: THREE.DoubleSide, unlit: true } : {})
        }
        return applyModelTextureOverride(parsedObject, displayTextureUrl, texOpts).then(() => {
          if (spec.billboardCutout) {
            let mapTex = null
            parsedObject.traverse((child) => {
              if (child.isMesh && child.material && child.material.map) mapTex = child.material.map
            })
            if (mapTex) adjustBillboardCutoutAspect(parsedObject, mapTex, spec)
            groundAndCenterModelRoot(parsedObject)
          }
        })
      }
    }).then(() => {
      if (spec.meshTextures) {
        return applyModelMeshTextures(parsedObject, spec.meshTextures, spec.meshTextureCutout)
      }
    }).then(() => {
      if (spec.meshColors) applyModelMeshColors(parsedObject, spec.meshColors)
    }).then(() => {
      if (!countMeshes(parsedObject)) {
        throw new Error('model has no meshes: ' + spec.key)
      }
      const wrapped = wrapSandModel(parsedObject)
      bindModelAnimation(wrapped, parsedObject, parsedClips, {
        staticPose: spec.staticPose || spec.bakeSkinnedToStatic,
        animHoldRatio: spec.animHoldRatio,
        spec
      })
      wrapped.userData.sandModelKey = spec.key
      wrapped.userData.sandModelUrl = spec.url
      if (spec.humanCharacter && parsedObject) {
        wrapped.userData.sandDefaultRotationY = parsedObject.rotation.y
      }
      variantTemplateCache[cacheKey] = wrapped
      delete variantLoading[cacheKey]
      return wrapped
    })
  }).catch((err) => {
    delete variantLoading[cacheKey]
    throw err
  })

  return variantLoading[cacheKey]
}

export function loadSandModel (modelKeyOrUrl) {
  const spec = getSandModelSpec(modelKeyOrUrl)
  if (!spec) return Promise.reject(new Error('missing model spec'))
  return buildSandModelVariant(spec)
}

const thumbImageCache = Object.create(null)

/** 预取 PNG 缩略图（轻量，不加载 FBX） */
export function preloadSandThumbImages (urls, concurrency = 6) {
  const list = [...new Set((urls || []).filter(Boolean))]
  if (!list.length) return Promise.resolve()

  const tasks = list.map((url) => {
    if (thumbImageCache[url]) return thumbImageCache[url]
    thumbImageCache[url] = new Promise((resolve) => {
      const img = new Image()
      img.decoding = 'async'
      img.onload = () => resolve(url)
      img.onerror = () => resolve(url)
      img.src = url
    })
    return thumbImageCache[url]
  })

  return runWithConcurrency(tasks, concurrency)
}

/** 选中沙具时后台预加载 3D 模型，放置时更快 */
export function prefetchSandModel (modelKeyOrUrl) {
  const spec = getSandModelSpec(modelKeyOrUrl)
  if (!spec) return Promise.resolve(null)
  return loadSandModel(spec.key).catch(() => null)
}

function runWithConcurrency (tasks, concurrency) {
  if (!tasks.length) return Promise.resolve()
  const limit = Math.max(1, concurrency || 1)
  return new Promise((resolve) => {
    let index = 0
    let active = 0
    let done = 0
    function next () {
      while (active < limit && index < tasks.length) {
        const task = tasks[index++]
        active++
        Promise.resolve(task).finally(() => {
          active--
          done++
          if (done >= tasks.length) resolve()
          else next()
        })
      }
    }
    next()
  })
}

export function preloadSandModels (keysOrUrls, options = {}) {
  const list = (keysOrUrls || []).filter(Boolean)
  if (!list.length) return Promise.resolve()

  const concurrency = options.concurrency == null ? 3 : options.concurrency
  const rawLoads = Object.create(null)
  const variantKeys = []

  list.forEach((item) => {
    const spec = getSandModelSpec(item)
    if (!spec) return
    const urls = spec.modelParts && spec.modelParts.length ? spec.modelParts : [spec.url]
    urls.forEach((url) => {
      const rawKey = rawFbxCacheKey(url, spec.texture || null)
      if (!rawLoads[rawKey]) rawLoads[rawKey] = loadRawFbx(url, spec.texture || null)
    })
    const rawOnly = spec.key === spec.url && !spec.meshKeep && !spec.texture && !(spec.modelParts && spec.modelParts.length)
    if (!rawOnly) variantKeys.push(spec.key)
  })

  const rawTasks = Object.values(rawLoads)
  return runWithConcurrency(rawTasks, concurrency).then(() => {
    const variantTasks = variantKeys.map((key) => loadSandModel(key).catch(() => null))
    return runWithConcurrency(variantTasks, concurrency)
  })
}

/** 生成沙具列表用的 3D 缩略图（与沙盘放置的模型一致） */
export function renderModelThumbnail (modelKeyOrUrl, size = 96) {
  const spec = getSandModelSpec(modelKeyOrUrl)
  if (!spec) return null
  const cacheKey = variantCacheKey(spec)
  const template = variantTemplateCache[cacheKey]
  if (!template) return null

  const src = template.userData.animModel || template.children[0]
  if (!src) return null

  const model = SkeletonUtils.clone(src)
  model.position.set(0, 0, 0)
  model.updateMatrixWorld(true)

  const pivot = new THREE.Group()
  pivot.add(model)

  const scene = new THREE.Scene()
  const thumbBg = 0x90caf9
  scene.background = new THREE.Color(thumbBg)
  scene.add(new THREE.AmbientLight(0xffffff, 0.95))
  const key = new THREE.DirectionalLight(0xffffff, 0.85)
  key.position.set(2.5, 5, 4)
  scene.add(key)
  const fill = new THREE.DirectionalLight(0xfff0d0, 0.35)
  fill.position.set(-2, 1, -3)
  scene.add(fill)
  scene.add(pivot)

  pivot.updateMatrixWorld(true)
  const box = new THREE.Box3().setFromObject(pivot)
  const center = box.getCenter(new THREE.Vector3())
  const boxSize = box.getSize(new THREE.Vector3())
  pivot.position.sub(center)
  pivot.updateMatrixWorld(true)

  const maxDim = Math.max(boxSize.x, boxSize.y, boxSize.z, 0.001)
  const cam = new THREE.PerspectiveCamera(32, 1, 0.01, 100)
  const padding = 1.18
  const fovRad = (cam.fov * Math.PI) / 180
  const dist = (maxDim / 2) / Math.tan(fovRad / 2) * padding
  cam.position.set(dist * 0.22, dist * 0.18, dist)
  cam.lookAt(0, boxSize.y * 0.06, 0)
  cam.updateProjectionMatrix()

  const renderer = new THREE.WebGLRenderer({
    alpha: false,
    antialias: true,
    preserveDrawingBuffer: true
  })
  renderer.setSize(size, size)
  if (THREE.sRGBEncoding !== undefined) renderer.outputEncoding = THREE.sRGBEncoding
  renderer.render(scene, cam)
  const dataUrl = renderer.domElement.toDataURL('image/png')

  renderer.dispose()
  scene.traverse((child) => {
    if (child.geometry) child.geometry.dispose()
    if (child.material) {
      const mats = Array.isArray(child.material) ? child.material : [child.material]
      mats.forEach((m) => m.dispose())
    }
  })

  return dataUrl
}

function copyAnimHoldMeta (targetWrapper, sourceWrapper, spec) {
  targetWrapper.userData.animClips = (sourceWrapper.userData.animClips || []).map((c) => c.clone())
  targetWrapper.userData.sandModelSpec = sourceWrapper.userData.sandModelSpec || spec
  targetWrapper.userData.sandAnimHold = !!sourceWrapper.userData.sandAnimHold
  targetWrapper.userData.sandAnimHoldRatio = sourceWrapper.userData.sandAnimHoldRatio
  targetWrapper.userData.animClipName = sourceWrapper.userData.animClipName
  targetWrapper.userData.sandStaticPose = !!sourceWrapper.userData.sandStaticPose
  targetWrapper.userData.sandPoseBaked = !!sourceWrapper.userData.sandPoseBaked
  targetWrapper.userData.animMixer = null
  targetWrapper.userData.animAction = null
}

export function cloneSandModel (modelKeyOrUrl, loadedTemplate) {
  const spec = getSandModelSpec(modelKeyOrUrl)
  if (!spec) return null
  const cacheKey = variantCacheKey(spec)
  const template = loadedTemplate || variantTemplateCache[cacheKey]
  if (!template) return null

  const templateModel = template.userData.animModel || template.children[0]
  const clonedModel = SkeletonUtils.clone(templateModel)
  clonedModel.position.copy(templateModel.position)
  clonedModel.scale.copy(templateModel.scale)
  clonedModel.rotation.copy(templateModel.rotation)
  cloneMeshMaterials(clonedModel)
  clonedModel.traverse((child) => {
    if (child.isSkinnedMesh && child.skeleton) child.skeleton.update()
  })
  fixVolumetricFishFacing(clonedModel, spec)
  ensureLayFlatCritterPose(clonedModel, spec)

  const wrapped = wrapSandModel(clonedModel)
  wrapped.userData.animClips = (template.userData.animClips || []).map((c) => c.clone())
  if (
    spec &&
    spec.animHoldRatio != null &&
    !spec.bakeSkinnedToStatic &&
    wrapped.userData.animClips.length &&
    collectionHasSkinnedMesh(clonedModel)
  ) {
    sampleSkinnedPoseAtTime(
      clonedModel,
      wrapped.userData.animClips,
      spec.animHoldRatio,
      wrapped,
      spec
    )
    wrapped.userData.sandAnimHold = true
    wrapped.userData.sandAnimHoldRatio = spec.animHoldRatio
    wrapped.userData.sandPoseBaked = true
  }
  wrapped.userData.animModel = clonedModel
  wrapped.userData.sandModelKey = spec.key
  wrapped.userData.sandModelUrl = spec.url
  wrapped.userData.sandModelSpec = spec
  copyAnimHoldMeta(wrapped, template, spec)

  // 确保克隆模型正确贴地：调用 groundAndCenterModelRoot 调整 Y 位置
  if (spec && spec.humanCharacter && collectionHasSkinnedMesh(clonedModel)) {
    clonedModel.updateMatrixWorld(true)
    const minY = getMinVertexYInParentSpace(clonedModel, 2400)
    if (isFinite(minY)) {
      clonedModel.position.y -= minY
      clonedModel.updateMatrixWorld(true)
    }
  }

  if (spec && spec.layFlatOnSand) {
    clonedModel.traverse((child) => {
      if (child.isSkinnedMesh && child.skeleton) child.skeleton.update()
    })
    ensureLayFlatCritterPose(clonedModel, spec)
  }

  // 修复克隆模型的贴地问题：对于有 animHoldRatio 的模型，
  // 重新采样骨骼姿态并确保 Mixer 正确设置
  if (
    spec &&
    spec.animHoldRatio != null &&
    !spec.bakeSkinnedToStatic &&
    wrapped.userData.animClips &&
    wrapped.userData.animClips.length &&
    collectionHasSkinnedMesh(clonedModel)
  ) {
    const clips = wrapped.userData.animClips
    const mixer = new THREE.AnimationMixer(clonedModel)
    const clip = pickSpecAnimationClip(clips, spec)
    if (clip) {
      const action = mixer.clipAction(clip)
      action.play()
      const t = Math.max(0, clip.duration * spec.animHoldRatio)
      mixer.update(t)
      action.paused = true
      // 确保 Mixer 被正确设置到 wrapped，而不是被 copyAnimHoldMeta 覆盖
      wrapped.userData.animMixer = mixer
      wrapped.userData.animAction = action
      wrapped.userData.animClipName = clip.name
    }
  }

  return wrapped
}

/** 在 render 循环里调用，驱动沙具骨骼动画 */
export function updateSandPropAnimations (propsGroup, deltaSec) {
  if (!propsGroup || !deltaSec) return
  propsGroup.children.forEach((prop) => {
    if (prop.userData.sandAnimHold) return
    const mixer = prop.userData.animMixer
    if (mixer) mixer.update(deltaSec)
  })
}

export function disposeSandProp (prop) {
  if (!prop) return
  if (prop.userData.animMixer) {
    prop.userData.animMixer.stopAllAction()
    prop.userData.animMixer = null
  }
  if (
    prop.userData.sandPropKind === 'model' ||
    prop.userData.sandPropKind === 'preview-plane'
  ) {
    prop.traverse((child) => {
      if (child.geometry) child.geometry.dispose()
      if (child.material) {
        const mats = Array.isArray(child.material) ? child.material : [child.material]
        mats.forEach((m) => {
          if (m.map) m.map.dispose()
          m.dispose()
        })
      }
    })
    return
  }
  if (prop.material) {
    if (prop.material.map) prop.material.map.dispose()
    prop.material.dispose()
  }
}



// WEBPACK FOOTER //
// ./src/utils/sandModelLoader.js